import { defineStore } from 'pinia'
import { watch } from 'vue'

import { StarPrinterConnect } from '@noona-hq/salescloud-star-printer-connect'
import * as Sentry from '@sentry/vue'
import axios from 'axios'
import currencyFormatter from 'currency-formatter'
import { v4 as uuidV4 } from 'uuid'
import i18n from '../plugins/i18n'

import { DateTime } from 'luxon'
import { GET_CUSTOMER_DISCOUNT, GET_LOCATION_BLOCKS } from '../graphql/queries'
import { apolloClient } from '../plugins/apollo'

// Global values
const defaultDelayTime = 5000

// Payment namespace constants
export const VERIFONE_NAMESPACE = 'verifone'
export const VERIFONE_P_NAMESPACE = 'verifone_p'
export const TEYA_POS_NAMESPACE = 'teya_pos_link'
export const ADYEN_POS_NAMESPACE = 'adyen-pos'

// Cloud terminal payment methods - these use /resolve endpoint for pending payment checks
export const CLOUD_TERMINAL_NAMESPACES = [TEYA_POS_NAMESPACE, ADYEN_POS_NAMESPACE]

// All supported card reader payment methods (native + cloud terminals)
export const SUPPORTED_CARD_READER_NAMESPACES = [VERIFONE_NAMESPACE, VERIFONE_P_NAMESPACE, ...CLOUD_TERMINAL_NAMESPACES]

// Add this near the top with other constants
const localeMap = {
  en: 'en-US',
  is: 'is-IS',
  da: 'da-DK',
  pt: 'pt-PT',
  sv: 'sv-SE',
}

// Helpers
/**
 * @name generateServiceId
 * @description Generates a 10 character alphanumeric service ID
 * @return {string}
 */
const generateServiceId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < 10; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * @name getFormattedPrice
 * @description Formats price
 * @param price
 * @param kioskObject
 * @return {string|null|*}
 */
const getFormattedPrice = (price, kioskObject) => {
  if (price && typeof price.amount !== 'undefined' && !isNaN(Number(price.amount)) && price.currency_code) {
    // Set initial values
    let amount = Number(price.amount) || 0
    const currency = price.currency_code || 'ISK'
    let decimalDigits = 0
    let thousandsSeparator = '.'
    let precision = 0
    let decimalSeparator = ','

    // Find currency information
    const foundCurrency = currencyFormatter.findCurrency(currency)

    // Set values if currency found
    if (foundCurrency && foundCurrency.decimalDigits > 0) {
      decimalDigits = foundCurrency.decimalDigits
      thousandsSeparator = foundCurrency.thousandsSeparator
      decimalSeparator = foundCurrency.decimalSeparator
      precision = foundCurrency.decimalDigits
    }

    amount = amount / Math.pow(10, decimalDigits)

    if (kioskObject && kioskObject.showPricesWithCurrencyCode === true) {
      let formattedAmount = currencyFormatter.format(amount, {
        thousandsSeparator: thousandsSeparator,
        decimalSeparator: decimalSeparator,
        precision: precision,
      })

      if (currency === 'ISK') {
        formattedAmount = formattedAmount.toString().replace(/,/g, '.')
      }

      return `${formattedAmount} ${currency}`
    }

    return currencyFormatter.format(amount, { code: currency })
  } else {
    return null
  }
}

/**
 * @description Gets variations amount
 * @param variations
 * @return {number}
 */
const calculateVariationsTotalAmount = (variations) => {
  if (Array.isArray(variations)) {
    return variations.reduce((sum, variation) => {
      if (typeof variation !== 'undefined' && variation !== null) {
        if (typeof variation.price !== 'undefined' && variation.price !== null) {
          let variationQuantity = 1
          if (!isNaN(Number(variation.price.amount))) {
            if (!isNaN(Number(variation.quantity))) {
              variationQuantity = Number(variation.quantity)
            }
            return sum + variation.price.amount * variationQuantity
          }
        }
      }
      return sum
    }, 0)
  }
  return 0
}

/**
 * @description Gets a singular item amount
 * @param item
 * @return {number}
 */
const calculateItemAmount = (item) => {
  if (typeof item !== 'undefined' && item !== null) {
    if (typeof item.price !== 'undefined' && item.price !== null) {
      if (!isNaN(Number(item.price.amount))) {
        if (typeof item.sale_price !== 'undefined' && item.sale_price !== null) {
          if (
            !isNaN(Number(item.sale_price.amount)) &&
            Number(item.sale_price.amount) > 0 &&
            Number(item.sale_price.amount) < Number(item.price.amount)
          ) {
            return Number(item.sale_price.amount)
          }
        }
        return Number(item.price.amount)
      }
    }
  }
  return 0
}

/**
 * @description Item price with selected variations and applicable discounts
 * @param item
 * @param quantity
 * @param selectedVariations
 * @return {{amount: number, currency_code: string}}
 */
const calculateItemCurrentPrice = (item, quantity = 1, selectedVariations = []) => {
  const total = {
    amount: 0,
    currency_code: 'ISK',
  }

  if (typeof item !== 'undefined' && item !== null) {
    const itemAmount = calculateItemAmount(item)
    let variationsAmount = calculateVariationsTotalAmount(selectedVariations)

    // Always include default variations that are not already in selectedVariations
    if (Array.isArray(item.default_variations)) {
      const defaultVariationsNotInSelectedVariations = item.default_variations.filter(
        (variation) => variation && !selectedVariations.some((v) => v && v.uuid === variation.uuid)
      )
      if (defaultVariationsNotInSelectedVariations.length > 0) {
        variationsAmount += calculateVariationsTotalAmount(defaultVariationsNotInSelectedVariations)
      }
    }

    // Making sure we detect correct currency code
    if (typeof item.price !== 'undefined' && item.price !== null) {
      if (typeof item.price.currency_code !== 'undefined' && item.price.currency_code !== null) {
        total.currency_code = item.price.currency_code
      }
    }

    total.amount = (itemAmount + variationsAmount) * quantity
  }

  return total
}

/**
 * @description Base item price, with default variations and no discounts, or sale price
 * @param item
 * @param quantity
 * @return {{amount: number, currency_code: string}}
 */
const calculateItemBasePrice = (item, quantity = 1) => {
  const total = {
    amount: 0,
    currency_code: 'ISK',
  }

  if (typeof item !== 'undefined' && item !== null) {
    let variationsAmount = 0

    if (Array.isArray(item.default_variations)) {
      variationsAmount = calculateVariationsTotalAmount(item.default_variations)
    }

    if (typeof item.price !== 'undefined' && item.price !== null) {
      if (!isNaN(Number(item.price.amount))) {
        total.amount += item.price.amount
      }
      total.currency_code = item.price.currency_code
    }

    total.amount += variationsAmount

    if (quantity > 0) {
      total.amount *= quantity
    }
  }

  return total
}

const calculateOrderTotal = (order) => {
  const total = {
    amount: 0,
    currency_code: 'ISK',
  }

  if (typeof order !== 'undefined' && order !== null) {
    if (Array.isArray(order.lineItems)) {
      total.amount = order.lineItems.reduce((sum, lineItem) => {
        if (typeof lineItem !== 'undefined' && lineItem !== null) {
          let finalAmount
          let currencyCode

          // If we have a discounted total price from PSR, use that instead of calculating manually
          if (lineItem.discountedTotalPrice && lineItem.discountedTotalPrice.amount >= 0) {
            finalAmount = lineItem.discountedTotalPrice.amount
            currencyCode = lineItem.discountedTotalPrice.currency_code
          } else {
            // Fallback to manual calculation for non-discounted items
            const lineItemCurrentPrice = calculateItemCurrentPrice(
              lineItem,
              lineItem.quantity,
              lineItem.selectedVariations
            )
            finalAmount = lineItemCurrentPrice.amount
            currencyCode = lineItemCurrentPrice.currency_code
          }

          total.currency_code = currencyCode
          return sum + finalAmount
        }
        return sum
      }, 0)
    }
  }

  return total
}

/**
 * Creates a time slot object from event data
 * @param {Object} eventData - Contains timestampInMillis and paddingInMillis
 * @param {number} quantity - Quantity for the time slot
 * @returns {Object} Time slot object
 */
const createTimeSlot = (eventData, quantity = 1) => ({
  startsAtTime: eventData.timestampInMillis / 1000,
  endsAtTime: Math.floor((eventData.timestampInMillis + eventData.paddingInMillis) / 1000),
  quantity,
})

/**
 * Creates a cart item for an event variation
 * @param {Object} lineItem - Original line item
 * @param {Object} variation - Variation to create cart item for
 * @returns {Object} Cart item
 */
const createEventVariationCartItem = (lineItem, variation) => ({
  uuid: lineItem.uuid,
  quantity: 1,
  variations: [variation],
  timeSlots: [createTimeSlot(lineItem.selectedTimeSlot)],
})

/**
 * Determines the appropriate price for a line item
 * @param {Object} lineItem - Line item with price information
 * @returns {Object|null} Price object
 */
const getLineItemPrice = (lineItem) => {
  if (!lineItem.price) return null

  const { price, sale_price } = lineItem
  const hasValidSalePrice =
    sale_price?.amount != null &&
    !isNaN(Number(sale_price.amount)) &&
    !isNaN(Number(price.amount)) &&
    Number(sale_price.amount) > 0 &&
    Number(sale_price.amount) < Number(price.amount)

  return hasValidSalePrice ? sale_price : price
}

/**
 * @name getCartItems
 * @description Gets cart items setup used for payment request
 * @param {Array} lineItems - Array of line items to process
 * @return {Array} Processed cart items
 */
const getCartItems = (lineItems) => {
  if (!Array.isArray(lineItems)) return []

  const items = []

  for (const lineItem of lineItems) {
    if (!lineItem) continue

    const isEvent = lineItem.type === 'event'
    const hasTimeSlot = lineItem.selectedTimeSlot != null
    const hasMultipleVariations = lineItem.selectedVariations.length > 1

    // Handle events with multiple variations separately
    if (isEvent && hasTimeSlot && hasMultipleVariations) {
      for (const variation of lineItem.selectedVariations) {
        items.push(createEventVariationCartItem(lineItem, variation))
      }
      continue
    }

    // Base cart item
    const cartItem = {
      uuid: lineItem.uuid,
      quantity: lineItem.quantity || 1,
      variations: lineItem.selectedVariations,
      discountPercent: lineItem.discountPercent || 0,
    }

    // Handle event time slots
    if (isEvent && hasTimeSlot) {
      cartItem.timeSlots = [createTimeSlot(lineItem.selectedTimeSlot, cartItem.quantity)]
    }

    // Add price
    const price = getLineItemPrice(lineItem)
    if (price) {
      cartItem.price = price
    }

    items.push(cartItem)
  }

  return items
}

/**
 * @name delay
 * @description Delay something for a set amount of milliseconds, generally used to wait for response
 * @param ms
 * @return {Promise<unknown>}
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * @name getDefaultHTMLReceipt
 * @description Default simple receipt HTML
 * @param organization
 * @param location
 * @param kiosk
 * @param deviceInfo
 * @param paymentResponse
 * @param order
 * @return {string}
 */

/**
 * @name getPaymentRequestUrl
 * @description Get the payment request URL from environment variable
 * @return {string}
 */
const getPaymentRequestUrl = () => {
  try {
    return import.meta.env.VITE_PAYMENT_REQUEST_URL || 'https://service-payment-request.salescloud.is/v/1'
  } catch (error) {
    console.error('[Store] getPaymentRequestUrl', error)
    return 'https://service-payment-request.salescloud.is/v/1'
  }
}

/**
 * @name getGraphUrl
 * @description Get the graph URL from environment variable
 * @return {string}
 */
const getGraphUrl = () => {
  try {
    return import.meta.env.VITE_GRAPHQL_URL || 'https://graph.salescloud.is'
  } catch (error) {
    console.error('[Store] getGraphUrl', error)
    return 'https://graph.salescloud.is'
  }
}

export const useStore = defineStore('store', {
  state: () => ({
    graphUrl: getGraphUrl(),
    paymentRequestUrl: getPaymentRequestUrl(),
    isLandscape: false,
    kioskUUID: null,
    previewValues: null,
    gettingInitialData: false,
    // organization: null,
    // channel: null,
    // locations: null,
    // categories: null,
    // items: null,
    // itemLocationBlocks: null,
    // variationLocationBlocks: null,
    order: null,
    selectedItem: null,
    paymentMethodInstances: null,
    showPaymentOptions: false,
    showPhoneInput: false,
    showNameAndPhoneInput: false,
    showUpsell: false,
    showPayerInfoInput: false,
    payerInfoFieldsInput: [],
    originalPaymentResponse: null,
    paymentResponse: null,
    showPackagingOptions: true,
    takeawayOnly: false,
    eatInOnly: false,
    showConfirmActivity: false,
    showPaymentMethodInput: false,
    requestName: false,
    requestPhone: false,
    requestEmail: false,
    latestCardReaderMessage: null,
    // showPricesWithCurrencyCode: false,
    // defaultLanguage: 'en',
    // enableSubCategories: false,
    //payerInfoRequests: null,
    // packagingOptions: ['eatIn', 'takeAway'],
    // allowedLanguages: null,
    // maintenanceMode: false,
    sessionToken: null,
    selectedCategory: null,
    selectedSubCategory: null,
    visibleCategory: null,
    socketURL: 'wss://ws.salescloud.is/subscriptions',
    socketClient: null,
    kiosk: null,
    primaryColor: null,
    accentColor: null,
    showingOrderLineItems: true,
    foodhallSelectedCategory: null,
    sessionStartTime: new Date(),
    touchSlidingActive: false,
    scanningQRCode: false,
    showInitialSetupScreen: true,
    hasNetworkConnectionError: false,
    showNoNetwork: false,
    hasNoNetwork: false,
    hasNoKioskData: false,
    supportedCardReaderPaymentMethodInstances: SUPPORTED_CARD_READER_NAMESPACES,
    systemTimeInMillis: null,
    checkoutInProgress: false,
    hasPrinterPaper: true, // Add cached printer status
    startPaymentDirectly: false, // Flag to start payment directly for single card reader
    cardReaderTransactionInFlight: false,

    // Coupon state
    showCouponDialog: false,
    couponCode: '',
    couponValidating: false,
    couponError: null,
    couponCalculation: null,

    // Payment deduplication state
    lastProcessedPaymentTransactionId: null,
    currentlyProcessingPaymentTransactionId: null,
  }),
  getters: {
    calculateCartTotal: (state) => (items) => {
      if (!Array.isArray(items)) {
        return 0
      }

      return items.reduce((total, item) => {
        let itemPrice = item.price?.amount || 0
        const quantity = item.quantity || 1

        // Apply discount if present
        if (item.discountPercent && item.discountPercent > 0) {
          // Apply discount using the formula: price * (1 - discount/100)
          itemPrice = Math.round(itemPrice * (1 - item.discountPercent / 100))
        }

        return total + itemPrice * quantity
      }, 0)
    },
    packagingTypes() {
      const types = []

      if (this.kiosk && Array.isArray(this.kiosk.packagingOptions) && this.kiosk.packagingOptions.length > 0) {
        for (const option of this.kiosk.packagingOptions) {
          if (option === 'eatIn') {
            types.push({
              label: 'Eat-In',
              key: 'eatIn',
              icon: 'mdi-silverware-fork-knife',
              image: null,
            })
          }

          if (option === 'takeAway') {
            types.push({
              label: 'Take-Away',
              key: 'takeAway',
              icon: 'mdi-shopping-outline',
              image: null,
            })
          }

          if (option === 'ticket') {
            types.push({
              label: 'Book now',
              key: 'ticket',
              icon: 'mdi mdi-ticket-confirmation-outline',
              image: null,
            })
          }
        }
      } else {
        types.push({
          label: 'Order-Here',
          key: 'eatIn',
          icon: 'mdi-cart-outline',
          image: null,
        })
      }

      return types
    },
    isPrinterConnected() {
      return this.kiosk.receiptPrinter !== null && this.kiosk.receiptPrinter !== undefined
    },
    getVariationLabel: () => (variation) => {
      if (typeof variation === 'undefined' || variation === null) {
        return ''
      }
      try {
        const key = 'variation:label:' + variation.uuid
        // te checks if translations exists
        if (i18n.global.te(key)) {
          return i18n.global.t(key)
        }
        return variation.label
      } catch (e) {
        return variation.label
      }
    },
    getAttributeName: () => (attribute) => {
      if (typeof attribute === 'undefined' || attribute === null) {
        return ''
      }

      try {
        const key = 'attribute:name:' + attribute.uuid
        if (i18n.global.te(key)) {
          return i18n.global.t(key)
        }
        return attribute.name
      } catch (e) {
        return attribute.name
      }
    },
    getCategoryName: () => (category) => {
      if (typeof category === 'undefined' || category === null) {
        return ''
      }

      try {
        const key = 'category:name:' + category.uuid
        if (i18n.global.te(key)) {
          return i18n.global.t(key)
        }
        return category.name
      } catch (e) {
        return category.name
      }
    },
    getCategoryDescription: () => (category) => {
      if (typeof category === 'undefined' || category === null) {
        return ''
      }

      try {
        const key = 'category:description:' + category.uuid
        if (i18n.global.te(key)) {
          return i18n.global.t(key)
        }
        return category.description
      } catch (e) {
        return category.description
      }
    },
    getItemTitle: () => (item) => {
      if (typeof item === 'undefined' || item === null) {
        return ''
      }
      try {
        const key = 'item:title:' + item.uuid
        if (i18n.global.te(key)) {
          return i18n.global.t(key)
        }
        return item.title
      } catch (e) {
        return item.title
      }
    },
    getItemDescription: () => (item) => {
      if (typeof item === 'undefined' || item === null) {
        return ''
      }
      if (typeof item.description === 'undefined' || item.description === null) {
        return ''
      }

      try {
        const key = 'item:description:value:' + item.uuid
        if (i18n.global.te(key)) {
          return i18n.global.t(key)
        }
        return item.description.value
      } catch (e) {
        return item.description.value
      }
    },
    getDefaultSelectedVariationsOfItem() {
      return (item) => {
        return item.default_variations.filter((v) => v && !this.isVariationBlocked(v))
      }
    },
    getPrimaryImageOfImages() {
      return (itemImages, size) => {
        if (itemImages !== null && itemImages !== undefined && itemImages.length > 0) {
          const firstImage = itemImages[0]
          if (typeof firstImage !== 'undefined' && firstImage !== null) {
            if (typeof firstImage.file !== 'undefined' && firstImage.file !== null) {
              if (typeof firstImage.file.sources !== 'undefined' && firstImage.file.sources !== null) {
                if (typeof firstImage.file.sources[size] !== 'undefined' && firstImage.file.sources[size] !== null) {
                  return firstImage.file.sources[size]
                }
              }
            }
          }
        }
        return ''
      }
    },
    getImagesOfItem() {
      return (item) => {
        if (typeof item !== 'undefined' && item !== null) {
          if (Array.isArray(item.images)) {
            return item.images
          }
        }
        return []
      }
    },
    getSelectedVariationsDescription() {
      return (selectedVariations) => {
        if (Array.isArray(selectedVariations) && selectedVariations.length > 0) {
          return selectedVariations
            .reduce((array, variation) => {
              if (typeof variation !== 'undefined' && variation !== null) {
                array.push(this.getVariationLabel(variation).trim())
              }
              return array
            }, [])
            .join(', ')
        }
        return ''
      }
    },
    getItemVariations() {
      return (item) => {
        if (typeof item !== 'undefined' && item !== null) {
          if (Array.isArray(item.variations)) {
            return item.variations
          }
        }

        return []
      }
    },
    getRequiredVariationAttributesOfItem() {
      return (variations) => {
        const requiredAttributes = []

        for (let i in variations) {
          const variation = variations[i]

          if (!variation) {
            continue
          }
          if (!variation.attribute) {
            continue
          }
          if (!variation.attribute.required) {
            continue
          }
          if (requiredAttributes.some((a) => a && a.uuid === variation.attribute.uuid)) {
            continue
          }

          requiredAttributes.push(variation.attribute)
        }

        return requiredAttributes
      }
    },
    getVariationsOfAttribute() {
      return (attribute, variations) => {
        return variations.filter(
          (variation) => variation.attribute !== null && variation.attribute !== undefined && variation.attribute.uuid === attribute.uuid
        )
      }
    },
    /**
     * @description Cheapest amount the customer is forced to add on top of the item price, for display only.
     * For every required variation set that has no selection yet we add its cheapest selectable option, so a
     * 0 kr. item behind two required sets is shown at what it actually costs instead of at zero. Sets that are
     * already covered, by a default or by the customer, are priced by the normal calculation and skipped here.
     * Optional sets never contribute, since the customer can buy the item without them.
     */
    getMinimumRequiredVariationsAmount() {
      return (item, selectedVariations = []) => {
        const variations = this.getItemVariations(item)
        const requiredAttributes = this.getRequiredVariationAttributesOfItem(variations)

        if (requiredAttributes.length === 0) {
          return 0
        }

        // The price calculations always add default variations, whether or not they are selected,
        // so a set holding a default is already accounted for
        const coveredVariations = [...selectedVariations]
        if (Array.isArray(item.default_variations)) {
          coveredVariations.push(...item.default_variations)
        }

        return requiredAttributes.reduce((sum, attribute) => {
          const isCovered = coveredVariations.some(
            (variation) => variation && variation.attribute && variation.attribute.uuid === attribute.uuid
          )
          if (isCovered) {
            return sum
          }

          const options = this.getVariationsOfAttribute(attribute, variations).filter(
            (variation) => variation && !this.isVariationBlocked(variation)
          )
          if (options.length === 0) {
            return sum
          }

          const cheapest = options.reduce((min, variation) => {
            let amount = 0
            if (variation.price && !isNaN(Number(variation.price.amount))) {
              amount = Number(variation.price.amount)
            }
            return amount < min ? amount : min
          }, Infinity)

          return sum + cheapest
        }, 0)
      }
    },
    canShowConfirmActivity() {
      // Never interrupt a payment: its 30s auto-reset would wipe order.lineItems out
      // from under the transaction. paymentResponse only covers this from the reader's
      // first message onwards, so the flag covers the wait before it.
      if (this.cardReaderTransactionInFlight) {
        return false
      }

      if (this.paymentResponse !== null && this.paymentResponse !== undefined) {
        return false
      }

      if (Object.keys(this.order ?? {}).length === 0) {
        return false
      }

      return true
    },
    paymentFailed() {
      if (this.paymentResponse === null || this.paymentResponse === undefined) {
        return false
      }

      if (this.paymentResponse.paymentTransaction === null || this.paymentResponse.paymentTransaction === undefined) {
        return false
      }

      return this.paymentResponse.paymentTransaction.status === 'failure'
    },
    awaitingPayment() {
      const transaction = this.paymentResponse?.paymentTransaction

      // Checked before the flag so a stale flag can never trap the customer here
      if (transaction?.status !== null && transaction?.status !== undefined && transaction.status !== 'pending') {
        return false
      }

      if (this.cardReaderTransactionInFlight) {
        return true
      }

      if (this.paymentResponse === null || this.paymentResponse === undefined) {
        return false
      }

      if (this.paymentResponse.paymentTransaction === null || this.paymentResponse.paymentTransaction === undefined) {
        return false
      }

      return this.paymentResponse.paymentTransaction.status === 'pending'
    },
    paymentSuccess() {
      if (this.paymentResponse === null || this.paymentResponse === undefined) {
        return false
      }

      if (this.paymentResponse.paymentTransaction === null || this.paymentResponse.paymentTransaction === undefined) {
        return false
      }

      return (
        this.paymentResponse.paymentTransaction.status === 'success' &&
        ((this.paymentResponse.orderSequence !== null && this.paymentResponse.orderSequence !== undefined) ||
          (this.order.sequence !== null && this.order.sequence !== null))
      )
    },
    orderConfirmationPending() {
      if (this.paymentResponse === null || this.paymentResponse === undefined) {
        return false
      }

      if (this.paymentResponse.paymentTransaction === null || this.paymentResponse.paymentTransaction === undefined) {
        return false
      }

      return (
        this.paymentResponse.paymentTransaction.status === 'success' &&
        (this.paymentResponse.orderSequence === null ||
          this.paymentResponse.orderSequence === undefined ||
          this.order.sequence === null ||
          this.order.sequence === undefined)
      )
    },
    itemBasePrice() {
      return (item, quantity) => calculateItemBasePrice(item, quantity)
    },
    getItemCurrentPrice() {
      return (item, quantity, selectedVariations) =>
        calculateItemCurrentPrice(item, quantity, selectedVariations)
    },
    formatPrice(state) {
      return (price) => getFormattedPrice(price, state.kiosk)
    },
    displayImage() {
      const displayImage = (image, type = 'original') => {
        if (typeof image !== 'undefined' && image !== null) {
          if (typeof image.file !== 'undefined' && image.file !== null) {
            if (typeof image.file.sources !== 'undefined' && image.file.sources !== null) {
              if (typeof image.file.sources[type] !== 'undefined' && image.file.sources[type] !== null) {
                return image.file.sources[type]
              }
            }
          }
        }
        return ''
      }
      return (image, type) => displayImage(image, type)
    },
    getCartTotal(state) {
      return calculateOrderTotal(state.order)
    },
    isVariationBlocked(state) {
      const isVariationBlocked = (variation) => {
        if (typeof state.kiosk !== 'undefined' && state.kiosk !== null && Array.isArray(state.kiosk.variationLocationBlocks)) {
          let locationUUID = null
          if (typeof state.order !== 'undefined' && state.order !== null) {
            if (typeof state.order.selectedLocation !== 'undefined' && state.order.selectedLocation !== null) {
              locationUUID = state.order.selectedLocation.uuid
            }
          }
          if (typeof locationUUID !== 'undefined' && locationUUID !== null) {
            const blocks = state.kiosk.variationLocationBlocks.filter((block) => block && block.location === locationUUID)
            if (typeof variation !== 'undefined' && variation !== null) {
              const variationIsBlocked = blocks.find((b) => b && b.variation === variation.uuid)
              return Boolean(variationIsBlocked)
            }
          }
        }
        return false
      }
      return (variation) => isVariationBlocked(variation)
    },
    isItemBlocked(state) {
      const isItemBlocked = (item) => {
        if (typeof state.kiosk !== 'undefined' && state.kiosk !== null && Array.isArray(state.kiosk.itemLocationBlocks)) {
          let locationUUID = null
          if (typeof state.order !== 'undefined' && state.order !== null) {
            if (typeof state.order.selectedLocation !== 'undefined' && state.order.selectedLocation !== null) {
              locationUUID = state.order.selectedLocation.uuid
            }
          }

          if (typeof locationUUID !== 'undefined' && locationUUID !== null) {
            const blocks = state.kiosk.itemLocationBlocks.filter((block) => block && block.location === locationUUID)
            if (typeof item !== 'undefined' && item !== null) {
              const itemIsBlocked = blocks.find((b) => b && b.item === item.uuid)
              return Boolean(itemIsBlocked)
            }
          }
        }
        return false
      }
      return (item) => isItemBlocked(item)
    },
    isItemOpen: (state) => {
      const isItemOpen = (item) => {
        if (item && item.openingHours && Array.isArray(item.openingHours) && item.openingHours.length > 0) {
          const localTime = state.sessionStartTime
          const formattedHour = localTime.toLocaleString('en', { hour: '2-digit', hour12: false }).slice(0, 2)
          const formattedMinutes = localTime.toLocaleString('en', { minute: '2-digit' }).slice(-2)
          let localTimeHHmm = formattedHour + formattedMinutes
          localTimeHHmm = parseInt(localTimeHHmm)
          if (
            !item.openingHours.some(
              (oh) =>
                parseInt(oh.day) === localTime.getDay() &&
                parseInt(oh.starthours) <= localTimeHHmm &&
                parseInt(oh.endhours) >= localTimeHHmm
            )
          ) {
            return false
          }
        }
        return true
      }
      return (item) => isItemOpen(item)
    },
    isCategoryOpen: (state) => {
      const isCategoryIsOpen = (category) => {
        if (category && category.opening_hours && Array.isArray(category.opening_hours)) {
          const localTime = state.sessionStartTime
          const formattedHour = localTime.toLocaleString('en', { hour: '2-digit', hour12: false }).slice(0, 2)
          const formattedMinutes = localTime.toLocaleString('en', { minute: '2-digit' }).slice(-2)
          let localTimeHHmm = formattedHour + formattedMinutes
          localTimeHHmm = parseInt(localTimeHHmm)
          if (
            !category.opening_hours.some(
              (oh) =>
                parseInt(oh.day) === localTime.getDay() &&
                parseInt(oh.starthours) <= localTimeHHmm &&
                parseInt(oh.endhours) >= localTimeHHmm
            )
          ) {
            return false
          }
        }
        return true
      }
      return (category) => isCategoryIsOpen(category)
    },
    getPaymentRequest(state) {
      // Create payment request
      const paymentRequest = (paymentMethodInstance, cartTotal) => {
        const request = {}

        let instance = paymentMethodInstance

        if (!instance) {
          instance = state.order.selectedPaymentMethod
        }

        if (!instance) {
          return
        }

        const paymentRequestObject = {}

        const requestUUID = uuidV4()
        paymentRequestObject.requestId = `KIOSK-${requestUUID}`

        const paymentMethodInstanceUUID = instance.uuid
        paymentRequestObject.namespace = instance.namespace
        paymentRequestObject.sequence = true
        paymentRequestObject.orderOrigin = 'kiosk'
        paymentRequestObject.orderFulfillment = this.order.selectedLocationPackagingType?.key ?? null
        paymentRequestObject.orderContactPhoneNumber = this.order.phoneNumber

        paymentRequestObject.isAppleyPay = false

        paymentRequestObject.details = {}

        if (paymentRequestObject.namespace === ADYEN_POS_NAMESPACE) {
          paymentRequestObject.details.serviceId = generateServiceId()
        }

        if (this.order?.selectedPaymentMethod?.uiFields) {
          let details = {}
          for (const x in this.order.selectedPaymentMethod.uiFields) {
            details[this.order.selectedPaymentMethod.uiFields[x].key] = this.order.selectedPaymentMethod.uiFields[x].value
            this.order.selectedPaymentMethod.uiFields[x].value = null
          }
          paymentRequestObject.details = details
        }

        if (state.order.billingInfo) {
          paymentRequestObject.billingInfo = state.order.billingInfo
        }

        if (state.kiosk.receiptPrinter) {
          paymentRequestObject.excludeQrCode = !!state.kiosk.receiptPrinter.excludeQrCode
        }

        const organizationUUID = state.kiosk.organization.uuid
        let channelUUID = state.kiosk.channel.uuid

        paymentRequestObject.amount = 0
        paymentRequestObject.currency_code = 'ISK'

        if (cartTotal) {
          paymentRequestObject.amount = cartTotal.amount
          paymentRequestObject.currency_code = cartTotal.currency_code
        }

        if (state.couponCalculation) {
          paymentRequestObject.couponCalculation = state.couponCalculation
        }

        if (state.order) {
          // Selected location
          if (state.order.selectedLocation) {
            paymentRequestObject.location = state.order.selectedLocation.uuid
          }

          // Order comment
          if (state.order.comment) {
            paymentRequestObject.comment = state.order.comment
          }

          // Coupon
          if (state.order.coupon) {
            paymentRequestObject.coupon = state.order.coupon.uuid
          }

          // Cart items
          paymentRequestObject.items = getCartItems(state.order.lineItems)
        }
        request.object = paymentRequestObject

        // Payment request url
        request.url = `${state.paymentRequestUrl}/${organizationUUID}/${channelUUID}/${paymentMethodInstanceUUID}`

        return request
      }
      return (paymentMethodInstance, cartTotal) => paymentRequest(paymentMethodInstance, cartTotal)
    },
    getCategorySubCategories(state) {
      const categorySubCategories = (category) => {
        if (!state.kiosk) {
          return []
        }
        const categories = []

        const locationUUID = state.order?.selectedLocation?.uuid ?? null
        const items = state.kiosk.items.filter((item) => {
          if (item) {
            if (Array.isArray(item.sellInLocations) && item.sellInLocations.length > 0 && locationUUID) {
              return item.sellInLocations.some((location) => location && location.uuid === locationUUID)
            }
            return true
          }
          return false
        })

        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          if (item && Array.isArray(item.categories)) {
            for (let categoryIndex = 0; categoryIndex < item.categories.length; categoryIndex++) {
              const itemCategory = item.categories[categoryIndex]
              if (itemCategory && !categories.some((c) => c && c.uuid === itemCategory.uuid)) {
                if (Array.isArray(itemCategory.parent) && itemCategory.parent.some((cat) => cat && cat.uuid === category.uuid)) {
                  categories.push(itemCategory)
                }
              }
            }
          }
        }

        return categories
      }
      return (category) => categorySubCategories(category)
    },
    categoryHasItems(state) {
      const categoryHasItems = (category) => {
        if (!state.kiosk) {
          return false
        }
        if (typeof category !== 'undefined' && category !== null) {
          if (Array.isArray(state.kiosk.items)) {
            const itemsInLocation = state.kiosk.items.filter((item) => {
              if (item) {
                if (Array.isArray(item.sellInLocations) && item.sellInLocations.length > 0 && state.order && state.order.selectedLocation) {
                  return item.sellInLocations.some((location) => location && location.uuid === state.order.selectedLocation.uuid)
                }
                return true
              }
              return false
            })
            return (
              itemsInLocation.filter(
                (item) => item && Array.isArray(item.categories) && item.categories.some((c) => c && c.uuid === category.uuid)
              ).length > 0
            )
          }
        }
        return true
      }
      return (category) => categoryHasItems(category)
    },
    canIncreaseQuantityOfVariant() {
      return (currentVariationsInAttributeQuantity, maxVariations) => {
        if (!maxVariations || maxVariations <= 0) return true
        return currentVariationsInAttributeQuantity < maxVariations
      }
    },

    isKioskActive() {
      return this.kiosk !== null && typeof this.kiosk !== 'undefined' && this.kiosk.active !== false
    },

    getNextOpeningHour() {
      if (!this.isKioskActive) {
        return null
      }

      if (this.kiosk.isOpen) {
        return null
      }

      const openingHours = this.kiosk.openingHours

      if (!openingHours) {
        return null
      }

      const timezone = openingHours.timezone ?? 'UTC'
      const currentDateTime = this.getAdjustedDateTime(timezone)
      const nextDay = currentDateTime.plus({ days: 1 })

      // We are not going to return next open day if tomorrow is a closed day, no need to check for today here
      const tomorrowIsAClosedDay = this.isClosedDay(nextDay, this.kiosk.openingHours.closedDates)
      if (tomorrowIsAClosedDay) {
        return null
      }

      // Check if today or tomorrow is a seasonal hour
      const hasNextSeasonalOpeningTime = this.getNextSeasonalOpeningTime(currentDateTime, nextDay, openingHours.seasonalHours)
      if (hasNextSeasonalOpeningTime) {
        return hasNextSeasonalOpeningTime
      }

      // Check if today or tomorrow is a weekday hour
      const hasNextWeekdayOpeningTime = this.getNextWeekdayOpeningTime(currentDateTime, nextDay, openingHours.weekdayHours, timezone)
      if (hasNextWeekdayOpeningTime) {
        return hasNextWeekdayOpeningTime
      }

      return null
    },
    getCurrentLocale() {
      return (locale) => localeMap[locale] || 'en-US'
    },
  },
  actions: {
    customLogger(level, message, payload) {
      try {
        console[level](message) // Log to the console
      } catch (e) {
        Sentry.captureException(e, { extra: payload })
      }
    },
    enableCheckout() {
      this.checkoutInProgress = true

      if (this.kiosk.upsellGroups && this.kiosk.upsellGroups.length > 0) {
        this.showUpsell = true
        this.showPayerInfoInput = false
      } else if (
        this.kiosk.payerInfoRequests &&
        Array.isArray(this.kiosk.payerInfoRequests) &&
        this.kiosk.payerInfoRequests.length > 0
      ) {
        this.showPayerInfoInput = true
        this.payerInfoFieldsInput = this.kiosk.payerInfoRequests
      } else {
        this.enableShowPaymentOptions()
      }
    },
    closeUpsell() {
      this.showUpsell = false
    },
    applyUpsell() {
      this.showUpsell = false

      if (this.kiosk.payerInfoRequests && Array.isArray(this.kiosk.payerInfoRequests) && this.kiosk.payerInfoRequests.length > 0) {
        this.showPayerInfoInput = true
        this.payerInfoFieldsInput = this.kiosk.payerInfoRequests
      } else {
        this.enableShowPaymentOptions()
      }
    },
    applyCustomerInformation(name, phone) {
      this.showNameAndPhoneInput = false
      this.order.billingInfo = {
        name,
        phone,
      }

      this.order.phoneNumber = phone
      this.enableShowPaymentOptions()
    },
    applyPhoneNumber(phone) {
      this.showPhoneInput = false
      this.order.phoneNumber = phone
      this.enableShowPaymentOptions()
    },
    applyPayerInfo(fields) {
      this.showPayerInfoInput = false

      const newBillingInfo = {}

      for (const field of fields) {
        if (!field) {
          continue
        }
        if (field.value === null || field.value.length <= 0) {
          continue
        }
        if (field.name === null || field.name.length <= 0) {
          continue
        }
        newBillingInfo[field.name.toLowerCase()] = field.value
      }

      if (newBillingInfo != null) {
        if (newBillingInfo.phone != null) {
          this.order.phoneNumber = newBillingInfo.phone
        }
        if (newBillingInfo.comment != null) {
          this.order.comment = newBillingInfo.comment
        }
      }
      this.order.billingInfo = newBillingInfo

      this.enableShowPaymentOptions()
    },
    createTransactionHash(transaction) {
      if (!transaction?.payload?.verifoneTransactionReply) {
        return null
      }

      const hashObject = {
        namespace: transaction.namespace,
        currency_code: transaction.currency_code,
      }

      if (transaction.payload?.paymentMethod?.merchant_id) {
        hashObject.merchant_id = transaction.payload.paymentMethod.merchant_id
      }

      if (transaction.payload?.verifoneTransactionReply) {
        hashObject.amount = transaction.payload.verifoneTransactionReply.amount
        hashObject.date = transaction.payload.verifoneTransactionReply.date
        hashObject.time = transaction.payload.verifoneTransactionReply.time
        hashObject.authorizationNumber = transaction.payload.verifoneTransactionReply.authorizationNumber
        hashObject.acquirerName = transaction.payload.verifoneTransactionReply.acquirerName
        hashObject.transactionInfo = transaction.payload.verifoneTransactionReply.transactionInfo
        hashObject.stan = transaction.payload.verifoneTransactionReply.stan
      }

      // Simple hash function
      const content = JSON.stringify(hashObject)
      let hash = 0
      for (let i = 0; i < content.length; i++) {
        const char = content.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash |= 0 // Convert to 32-bit integer
      }
      const unsigned = hash >>> 0
      return `hash_${unsigned.toString(36)}`
    },
    clearLastProcessedPayment() {
      this.currentlyProcessingPaymentTransactionId = null
      this.customLogger('info', 'Cleared last processed payment transaction ID')
    },
    processCardReaderPaymentRequest(paymentRequestResponse) {
      // No live attempt means this belongs to one we already abandoned - a watchdog
      // timeout, a rejected start, or a session that has moved on. Posting it would
      // charge whatever order is current now, potentially a different customer's, so
      // drop it and shout instead. If the reader did take the money despite the
      // cancel, this is the record support needs to reconcile it by hand.
      if (!this.cardReaderTransactionInFlight) {
        Sentry.captureMessage('Card reader response arrived for an abandoned attempt; not posting', {
          level: 'error',
          extra: { paymentRequestResponse },
        })
        this.customLogger('error', 'Card reader response for an abandoned attempt; not posting')
        return
      }

      // Cleared here rather than at the end so the duplicate and malformed returns
      // below release the screen too.
      this.endCardReaderTransaction()

      const transaction = paymentRequestResponse.paymentTransaction
      if (!transaction) {
        Sentry.captureMessage('processCardReaderPaymentRequest: missing paymentTransaction; ignoring.', {
          level: 'warning',
          extra: paymentRequestResponse,
        })
        return
      }

      const transactionKey = this.createTransactionHash(transaction)
      if (transactionKey) {
        // Early duplicate check - before any expensive operations
        if (this.lastProcessedPaymentTransactionId === transactionKey) {
          this.customLogger('info', 'Duplicate payment request detected - already processed', {
            transactionKey,
            message: 'Skipping duplicate payment request',
          })
          return
        }

        // Check if currently processing the same transaction
        if (this.currentlyProcessingPaymentTransactionId === transactionKey) {
          this.customLogger('info', 'Duplicate payment request detected - currently processing', {
            transactionKey,
            message: 'Skipping duplicate payment request - already in progress',
          })
          return
        }

        // Mark as currently processing immediately after the checks
        this.currentlyProcessingPaymentTransactionId = transactionKey
      }

      const urlString = `${this.paymentRequestUrl}/${this.kiosk.organization.uuid}/${this.kiosk.channel.uuid}/${this.kiosk.cardReaderPaymentMethodInstance.uuid}`
      this.customLogger('info', 'Processing card reader payment request', {
        url: urlString,
        transactionKey,
      })

      // Use the same cart items processing as ecom payments
      const items = getCartItems(this.order.lineItems)

      try {
        // Items are now processed by getCartItems function
        if (!items || items.length === 0) {
          Sentry.captureMessage('No items processed from order lineItems', { extra: this.order })
        }
      } catch (e) {
        Sentry.captureException(e, { extra: 'paymentRequestResponse' })
        this.customLogger('error', 'Error processing cart items for terminal payment request', paymentRequestResponse)
      }

      if (
        paymentRequestResponse.paymentTransaction &&
        paymentRequestResponse.paymentTransaction.currency_code !== null &&
        paymentRequestResponse.paymentTransaction.currency_code !== undefined &&
        paymentRequestResponse.paymentTransaction.currency_code === 'ISK' &&
        paymentRequestResponse.paymentTransaction.namespace !== VERIFONE_P_NAMESPACE
      ) {
        paymentRequestResponse.paymentTransaction.amount = paymentRequestResponse.paymentTransaction.amount / 100
      }

      let orderFulfillment = 'eatIn'

      if (
        this.order !== null &&
        this.order !== undefined &&
        this.order.selectedLocationPackagingType !== null &&
        this.order.selectedLocationPackagingType !== undefined
      ) {
        orderFulfillment = this.order.selectedLocationPackagingType.key
      }

      let phoneNumber = null

      if (this.order !== null && this.order !== undefined && this.order.phoneNumber !== null && this.order.phoneNumber !== undefined) {
        phoneNumber = this.order.phoneNumber
      }

      let locationUUID = null

      if (
        this.order !== null &&
        this.order !== undefined &&
        this.order.selectedLocation !== null &&
        this.order.selectedLocation !== undefined
      ) {
        locationUUID = this.order.selectedLocation.uuid
      }

      // Create a unique identifier combining UUID and current timestamp (in seconds)
      // for tracking payment requests between kiosk and payment service
      const entangleKey = `${uuidV4()}:${Date.now() / 1000}`
      const body = {
        namespace: paymentRequestResponse.paymentTransaction.namespace,
        amount: paymentRequestResponse.paymentTransaction.amount,
        currency_code: paymentRequestResponse.paymentTransaction.currency_code,
        orderOrigin: 'kiosk',
        orderFulfillment,
        orderContactPhoneNumber: phoneNumber,
        details: paymentRequestResponse.paymentTransaction.payload,
        items,
        location: locationUUID,
        sequence: true,
        skipItemValidation: true,
        entangleKey,
      }

      // Add coupon calculation if present (same as ecom payments)
      if (this.couponCalculation) {
        body.couponCalculation = this.couponCalculation
      }
      const headers = { 'Content-Type': 'application/json' }

      if (this.kiosk.enableIdempotentPaymentRequests) {
        const newEntangleKey = `${uuidV4()}:${Date.now() / 1000}`
        body.entangleKey = newEntangleKey
        headers['x-idempotency'] = 'true'
      }

      if (this.order?.billingInfo) {
        body.billingInfo = this.order.billingInfo
      }

      if (this.kiosk.receiptPrinter) {
        body.excludeQrCode = !!this.kiosk.receiptPrinter.excludeQrCode
      }

      // The amount was fixed when the reader started, the items are only being read
      // now. Still post on a mismatch - the money is already taken, and dropping the
      // request would lose the payment record entirely - but never silently.
      const expectedTotal = calculateOrderTotal(this.order)
      if (expectedTotal.amount !== body.amount) {
        Sentry.captureMessage('Card reader payment amount does not cover the order line items', {
          level: 'error',
          extra: {
            paidAmount: body.amount,
            orderTotal: expectedTotal.amount,
            difference: expectedTotal.amount - body.amount,
            currencyCode: body.currency_code,
            namespace: body.namespace,
            lineItemCount: Array.isArray(items) ? items.length : null,
            items,
            entangleKey: body.entangleKey,
            transactionKey,
          },
        })
        this.customLogger('error', 'Card reader payment amount does not cover the order line items', {
          paidAmount: body.amount,
          orderTotal: expectedTotal.amount,
        })
      }

      const url = new URL(urlString)
      const domain = url.hostname
      const path = url.pathname + url.search
      const data = body

      // Sentry has no default integrations, so console output never reaches it
      Sentry.addBreadcrumb({
        category: 'payment',
        level: 'info',
        message: 'Sending card reader payment to api',
        data: {
          amount: body.amount,
          currency_code: body.currency_code,
          namespace: body.namespace,
          lineItemCount: Array.isArray(items) ? items.length : null,
          location: body.location,
          entangleKey: body.entangleKey,
        },
      })

      this.customLogger('info', 'Sending card reader payment to api', body)
      this.addPostRequestJob(domain, path, data, headers, 100, 500)
      this.lastProcessedPaymentTransactionId = transactionKey
    },
    enableShowPaymentOptions() {
      // Check if we should skip payment options screen for single card reader payment method
      if (this.shouldSkipPaymentOptionsForSingleCardReader()) {
        this.customLogger('info', 'Skipping PaymentOptions screen - single card reader payment method detected')
        this.selectCardReaderPaymentMethodDirectly()
      } else {
        this.showPaymentOptions = true
      }
    },
    selectCardReaderPaymentMethodDirectly() {
      // Simulate the same logic as PaymentOptions.vue selectPaymentMethod()
      const instance = this.kiosk.cardReaderPaymentMethodInstance
      if (!this.order) {
        return // No order, no payment method to select
      }
      this.order.selectedPaymentMethod = instance

      if (
        instance.uiFields !== null &&
        instance.uiFields !== undefined &&
        Array.isArray(instance.uiFields) &&
        instance.uiFields.length > 0
      ) {
        this.showPaymentMethodInput = true
      } else {
        // Start payment directly using the same method as PaymentOptions component
        this.startDirectPayment()
      }
    },
    startDirectPayment() {
      // Set the flag that MainView watches to start payment (for all payment types)
      this.startPaymentDirectly = true
    },
    beginCardReaderTransaction() {
      this.cardReaderTransactionInFlight = true
    },
    endCardReaderTransaction() {
      this.cardReaderTransactionInFlight = false
    },
    shouldSkipPaymentOptionsForSingleCardReader() {
      // Check if card reader payment method exists and is supported
      const hasCardReaderPayment =
        this.kiosk.cardReaderPaymentMethodInstance !== null &&
        this.kiosk.cardReaderPaymentMethodInstance !== undefined &&
        this.kiosk.cardReaderPaymentMethodInstance.namespace !== null &&
        this.kiosk.cardReaderPaymentMethodInstance.namespace !== undefined &&
        this.supportedCardReaderPaymentMethodInstances.includes(this.kiosk.cardReaderPaymentMethodInstance.namespace)

      if (!hasCardReaderPayment) {
        return false
      }

      // Check if there are other payment methods available
      let otherPaymentMethodsCount = 0

      if (
        this.kiosk.allowDifferentPaymentMethods === true &&
        this.kiosk.channel &&
        Array.isArray(this.kiosk.channel.acceptedPaymentMethodInstances)
      ) {
        // Count non-card-reader payment methods
        otherPaymentMethodsCount = this.kiosk.channel.acceptedPaymentMethodInstances.filter(
          (instance) => !this.supportedCardReaderPaymentMethodInstances.includes(instance.namespace)
        ).length
      }

      // Skip if only card reader payment method is available (no other payment methods)
      return otherPaymentMethodsCount === 0
    },
    selectItem(item) {
      // Belt to the z-index braces: the pending screen covers this now, but don't let
      // the dialog open behind it and edit the cart the payment is already priced on
      if (this.cardReaderTransactionInFlight) {
        return
      }
      this.selectedItem = item
    },
    autoSelectDefaultItemIfConfigured() {
      if (this.selectedItem) return
      const targetUuid = this.kiosk?.defaultSelectedItem
      if (!targetUuid || !Array.isArray(this.kiosk?.items)) return
      const target = this.kiosk.items.find(item => item?.uuid === targetUuid)
      if (target) {
        this.selectItem(target)
      }
    },
    updateSessionStartTime() {
      let timezone = 'UTC'
      if (typeof this.order !== 'undefined' && this.order !== null) {
        if (typeof this.order.selectedLocation !== 'undefined' && this.order.selectedLocation !== null) {
          if (typeof this.order.selectedLocation.timezone !== 'undefined' && this.order.selectedLocation.timezone !== null) {
            timezone = this.order.selectedLocation.timezone
          }
        }
      } else if (typeof this.selectedLocation !== 'undefined' && this.selectedLocation !== null) {
        if (typeof this.selectedLocation.timezone !== 'undefined' && this.selectedLocation.timezone !== null) {
          timezone = this.selectedLocation.timezone
        }
      }
      const date = new Date(new Date().toLocaleString('en', { timeZone: timezone }))
      this.sessionStartTime = date
    },
    selectPackaging(packaging) {
      if (!this.order) {
        this.order = {}
      }

      this.order.selectedLocationPackagingType = packaging

      // After the customer picks a packaging option on the front page,
      // jump straight to the default product's details view if configured.
      this.autoSelectDefaultItemIfConfigured()
    },
    setSelectedSubCategory(selectedSubCategory) {
      this.selectedSubCategory = selectedSubCategory
    },
    setSessionToken(sessionToken) {
      this.sessionToken = sessionToken
    },
    setIsLandscape(isLandscape) {
      this.isLandscape = isLandscape
    },
    subscribeToKiosk() {
      /*
      const body = {
        query:
          'subscription kiosk($uuid: String) {\n' +
          '   kiosk(uuid: $uuid) {\n' +
          '       uuid\n' +
          '       title\n' +
          '       orientation\n' +
          '       organization {\n' +
          '           uuid\n' +
          '           label\n' +
          '           phone\n' +
          '           email\n' +
          '           ssn\n' +
          '           description\n' +
          '           primary_color\n' +
          '           accent_color\n' +
          '           address {\n' +
          '               name_line\n' +
          '               thoroughfare\n' +
          '               locality\n' +
          '               country\n' +
          '               postal_code\n' +
          '           }\n' +
          '           settings {\n' +
          '               booleans {\n' +
          '                   key\n' +
          '                   value\n' +
          '               }\n' +
          '           }\n' +
          '           defaultCurrency {\n' +
          '               code\n' +
          '               name\n' +
          '           }\n' +
          '           defaultTimezone\n' +
          '           logo {\n' +
          '               file {\n' +
          '                   sources {\n' +
          '                       original\n' +
          '                       large\n' +
          '                       medium\n' +
          '                       thumbnail\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       channel {\n' +
          '           uuid\n' +
          '           label\n' +
          '           ept\n' +
          '           data\n' +
          '           acceptedPaymentMethodInstances {\n' +
          '               uuid\n ' +
          '               title\n' +
          '               namespace\n' +
          '               icon\n' +
          '               schemeIcon\n' +
          '               isApplePayCapable\n' +
          '               weight\n' +
          '               terminal\n' +
          '               checkout\n' +
          '               uiFields {\n' +
          '                   key\n' +
          '                   type\n' +
          '                   label\n' +
          '                   description\n' +
          '                   required\n' +
          '                   value\n' +
          '                   cols\n' +
          '                   placeholder\n' +
          '                   hint\n' +
          '                   options {\n' +
          '                       key\n' +
          '                       label\n' +
          '                   }\n' +
          '                   multiple\n' +
          '                   minLength\n' +
          '                   maxLength\n' +
          '                   weight\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       locations {\n' +
          '           uuid\n' +
          '           label\n' +
          '           description\n' +
          '           timezone\n' +
          '           isOpen\n' +
          '           taxId\n' +
          '           vatId\n' +
          '           preferredChannel {\n' +
          '               uuid\n' +
          '               label\n' +
          '               ept\n' +
          '               data\n' +
          '               acceptedPaymentMethodInstances {\n' +
          '                   uuid\n' +
          '                   title\n' +
          '                   namespace\n' +
          '                   icon\n' +
          '                   schemeIcon\n' +
          '                   isApplePayCapable\n' +
          '                   weight\n' +
          '                   terminal\n' +
          '                   checkout\n' +
          '                   uiFields {\n' +
          '                       key\n' +
          '                       type\n' +
          '                       label\n' +
          '                       description\n' +
          '                       required\n' +
          '                       value\n' +
          '                       cols\n' +
          '                       placeholder\n' +
          '                       hint\n' +
          '                       options {\n' +
          '                       key\n' +
          '                       label\n' +
          '                   }\n' +
          '                   multiple\n' +
          '                   minLength\n' +
          '                   maxLength\n' +
          '                   weight\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       categories {\n' +
          '           uuid\n' +
          '           name\n' +
          '           weight\n' +
          '       }\n' +
          '       items {\n' +
          '           uuid\n' +
          '           title\n' +
          '           description {\n' +
          '               value\n' +
          '               summary\n' +
          '           }\n' +
          '           price {\n' +
          '               amount\n' +
          '               currency_code\n' +
          '           }\n' +
          '           sale_price {\n' +
          '               amount\n' +
          '               currency_code' +
          '           }\n' +
          '           sorting_weight\n' +
          '           images {\n' +
          '               file {\n' +
          '                   sources {\n' +
          '                       original\n' +
          '                       large\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '           variations {\n' +
          '               uuid\n' +
          '               label\n' +
          '               sorting_weight\n' +
          '               images {\n' +
          '                   file {\n' +
          '                     sources {\n' +
          '                       large\n' +
          '                     }\n' +
          '                   }\n' +
          '               }\n' +
          '               attribute {\n' +
          '                   uuid\n' +
          '                   name\n' +
          '                   required\n' +
          '                   single\n' +
          '                   multiple\n' +
          '                   maxVariations\n' +
          '                   allowSplitByHalf\n' +
          '                   weight\n' +
          '               }\n' +
          '               price {\n' +
          '                   amount\n' +
          '                   currency_code\n' +
          '               }\n' +
          '           }\n' +
          '           default_variations {\n' +
          '               uuid\n' +
          '               label\n' +
          '               attribute {\n' +
          '                   uuid\n' +
          '                   name\n' +
          '                   required\n' +
          '                   single\n' +
          '                   multiple\n' +
          '                   maxVariations\n' +
          '                   allowSplitByHalf\n' +
          '                   weight\n' +
          '               }\n' +
          '               price {\n' +
          '                   amount\n' +
          '                   currency_code\n' +
          '               }\n' +
          '           }\n' +
          '           categories {\n' +
          '               uuid\n' +
          '               name\n' +
          '               description\n' +
          '               weight\n' +
          '               displayInLocations\n' +
          '               displayOnline\n' +
          '               parent {\n' +
          '                   uuid\n' +
          '                   name\n' +
          '               }\n' +
          '           }\n' +
          '           exclude_discounts\n' +
          '           locationPrices {\n' +
          '               location\n' +
          '               price {\n' +
          '                   amount\n' +
          '                   currency_code\n' +
          '               }\n' +
          '           }\n' +
          '           sellInKiosk\n' +
          '           sellInLocations {\n' +
          '               uuid\n' +
          '               label\n' +
          '           }\n' +
          '       }\n' +
          '       itemLocationBlocks {\n' +
          '           location\n' +
          '           item\n' +
          '       }\n' +
          '       variationLocationBlocks {\n' +
          '           location\n' +
          '           item\n' +
          '           variation\n' +
          '       }\n' +
          '       translations\n' +
          '       defaultLanguage\n' +
          '       showPricesWithCurrencyCode\n' +
          '       enableSubCategories\n' +
          '       packagingOptions\n' +
          '       payerInfoRequests\n' +
          '       allowedLanguages\n' +
          '   }\n' +
          '}',
        variables: {
          uuid: this.kioskUUID,
        },
      }

      this.socketClient.subscribe(body, {
        next: (data) => {
          this.kiosk = data.data.kiosk
        },
        error: (error) => {
          console.error(error)
        },
        complete: (error) => {
          console.log(error)
        },
      })
      */
    },
    getKioskData(uuid) {
      let kioskUUID = uuid

      if ((kioskUUID === null || kioskUUID === undefined) && this.kioskUUID !== null && this.kioskUUID !== undefined) {
        kioskUUID = this.kioskUUID
      }

      if (kioskUUID === null || kioskUUID === undefined) {
        return null
      }

      const body = {
        query:
          'query GetKioskDataByUUID($uuid: String!) {\n' +
          '   kiosk(uuid: $uuid) {\n' +
          '       uuid\n' +
          '       title\n' +
          '       isOpen\n' +
          '       closedMessage\n' +
          '       active\n' +
          '       enableIdempotentPaymentRequests\n' +
          '       availableTimeSlotsWindowInMinutes\n' +
          '       openingHours {\n' +
          '           enabled\n' +
          '           useLocation\n' +
          '           timezone\n' +
          '           closedDates\n' +
          '           weekdayHours {\n' +
          '               day\n' +
          '               starthours\n' +
          '               endhours\n' +
          '           }\n' +
          '           seasonalHours {\n' +
          '               startTimeInMillis\n' +
          '               endTimeInMillis\n' +
          '               starthours\n' +
          '               endhours\n' +
          '           }\n' +
          '       }\n' +
          '       organization {\n' +
          '           uuid\n' +
          '           label\n' +
          '           phone\n' +
          '           email\n' +
          '           ssn\n' +
          '           description\n' +
          '           primary_color\n' +
          '           accent_color\n' +
          '           address {\n' +
          '               name_line\n' +
          '               thoroughfare\n' +
          '               locality\n' +
          '               country\n' +
          '               postal_code\n' +
          '           }\n' +
          '           settings {\n' +
          '               booleans {\n' +
          '                   key\n' +
          '                   value\n' +
          '               }\n' +
          '           }\n' +
          '           defaultCurrency {\n' +
          '               code\n' +
          '               name\n' +
          '           }\n' +
          '           defaultTimezone\n' +
          '           logo {\n' +
          '               file {\n' +
          '                   sources {\n' +
          '                       original\n' +
          '                       large\n' +
          '                       medium\n' +
          '                       thumbnail\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '           cover {\n' +
          '               file {\n' +
          '                   sources {\n' +
          '                       original\n' +
          '                       large\n' +
          '                       medium\n' +
          '                       thumbnail\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '           featuredImages {\n' +
          '               file {\n' +
          '                   sources {\n' +
          '                       original\n' +
          '                       large\n' +
          '                       medium\n' +
          '                       thumbnail\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       receiptPrinter {\n' +
          '           ipAddress\n' +
          '           model\n' +
          '           excludeQrCode\n' +
          '       }\n' +
          '       allowDifferentPaymentMethods\n' +
          '       cardReaderPaymentMethodInstance {\n' +
          '           uuid\n' +
          '           title\n' +
          '           namespace\n' +
          '           icon\n' +
          '           data\n' +
          '           uiFields {\n' +
          '             key\n' +
          '             value\n' +
          '           }\n' +
          '       }\n' +
          '       channel {\n' +
          '           uuid\n' +
          '           label\n' +
          '           ept\n' +
          '           data\n' +
          '           acceptedPaymentMethodInstances {\n' +
          '               uuid\n ' +
          '               title\n' +
          '               namespace\n' +
          '               icon\n' +
          '               schemeIcon\n' +
          '               isApplePayCapable\n' +
          '               weight\n' +
          '               terminal\n' +
          '               checkout\n' +
          '               uiFields {\n' +
          '                   key\n' +
          '                   type\n' +
          '                   label\n' +
          '                   description\n' +
          '                   required\n' +
          '                   value\n' +
          '                   cols\n' +
          '                   placeholder\n' +
          '                   hint\n' +
          '                   options {\n' +
          '                       key\n' +
          '                       label\n' +
          '                   }\n' +
          '                   multiple\n' +
          '                   minLength\n' +
          '                   maxLength\n' +
          '                   weight\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       locations {\n' +
          '           uuid\n' +
          '           label\n' +
          '           description\n' +
          '           timezone\n' +
          '           isOpen\n' +
          '           taxId\n' +
          '           vatId\n' +
          '           preferredChannel {\n' +
          '               uuid\n' +
          '               label\n' +
          '               ept\n' +
          '               data\n' +
          '               acceptedPaymentMethodInstances {\n' +
          '                   uuid\n' +
          '                   title\n' +
          '                   namespace\n' +
          '                   icon\n' +
          '                   schemeIcon\n' +
          '                   isApplePayCapable\n' +
          '                   weight\n' +
          '                   terminal\n' +
          '                   checkout\n' +
          '                   uiFields {\n' +
          '                       key\n' +
          '                       type\n' +
          '                       label\n' +
          '                       description\n' +
          '                       required\n' +
          '                       value\n' +
          '                       cols\n' +
          '                       placeholder\n' +
          '                       hint\n' +
          '                       options {\n' +
          '                           key\n' +
          '                           label\n' +
          '                       }\n' +
          '                       multiple\n' +
          '                       minLength\n' +
          '                       maxLength\n' +
          '                       weight\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       categories {\n' +
          '           uuid\n' +
          '           name\n' +
          '           weight\n' +
          '           parent {\n' +
          '             uuid\n' +
          '             name\n' +
          '           }\n' +
          '           image {\n' +
          '             file {\n' +
          '               sources {\n' +
          '                 original\n' +
          '                 large\n' +
          '                 medium\n' +
          '                 thumbnail\n' +
          '               }\n' +
          '             }\n' +
          '           }\n' +
          '           opening_hours{\n' +
          '             day\n' +
          '             starthours\n' +
          '             endhours\n' +
          '           }\n' +
          '       }\n' +
          '       items {\n' +
          '           uuid\n' +
          '           title\n' +
          '           type\n' +
          '           reservationMode\n' +
          '           description {\n' +
          '               value\n' +
          '               summary\n' +
          '           }\n' +
          '           price {\n' +
          '               amount\n' +
          '               currency_code\n' +
          '           }\n' +
          '           sale_price {\n' +
          '               amount\n' +
          '               currency_code' +
          '           }\n' +
          '           sorting_weight\n' +
          '           images {\n' +
          '               file {\n' +
          '                   sources {\n' +
          '                       original\n' +
          '                       large\n' +
          '                       medium\n' +
          '                       thumbnail\n' +
          '                   }\n' +
          '               }\n' +
          '           }\n' +
          '           variations {\n' +
          '               uuid\n' +
          '               label\n' +
          '               sorting_weight\n' +
          '               images {\n' +
          '                   file {\n' +
          '                     sources {\n' +
          '                       large\n' +
          '                     }\n' +
          '                   }\n' +
          '               }\n' +
          '               attribute {\n' +
          '                   uuid\n' +
          '                   name\n' +
          '                   required\n' +
          '                   single\n' +
          '                   multiple\n' +
          '                   maxVariations\n' +
          '                   allowSplitByHalf\n' +
          '                   weight\n' +
          '               }\n' +
          '               price {\n' +
          '                   amount\n' +
          '                   currency_code\n' +
          '               }\n' +
          '           }\n' +
          '           default_variations {\n' +
          '               uuid\n' +
          '               label\n' +
          '               attribute {\n' +
          '                   uuid\n' +
          '                   name\n' +
          '                   required\n' +
          '                   single\n' +
          '                   multiple\n' +
          '                   maxVariations\n' +
          '                   allowSplitByHalf\n' +
          '                   weight\n' +
          '               }\n' +
          '               price {\n' +
          '                   amount\n' +
          '                   currency_code\n' +
          '               }\n' +
          '           }\n' +
          '           categories {\n' +
          '               uuid\n' +
          '               name\n' +
          '               description\n' +
          '               weight\n' +
          '               displayInLocations\n' +
          '               displayOnline\n' +
          '               parent {\n' +
          '                   uuid\n' +
          '                   name\n' +
          '               }\n' +
          '               image {\n' +
          '                 file {\n' +
          '                   sources {\n' +
          '                     original\n' +
          '                     large\n' +
          '                     medium\n' +
          '                     thumbnail\n' +
          '                   }\n' +
          '                 }\n' +
          '               }\n' +
          '           }\n' +
          '           exclude_discounts\n' +
          '           locationPrices {\n' +
          '               location\n' +
          '               price {\n' +
          '                   amount\n' +
          '                   currency_code\n' +
          '               }\n' +
          '           }\n' +
          '           sellInKiosk\n' +
          '           sellInLocations {\n' +
          '               uuid\n' +
          '               label\n' +
          '           }\n' +
          '           openingHours{\n' +
          '               day\n' +
          '               starthours\n' +
          '               endhours\n' +
          '           }\n' +
          '       }\n' +
          '       itemLocationBlocks {\n' +
          '           location\n' +
          '           item\n' +
          '       }\n' +
          '       variationLocationBlocks {\n' +
          '           location\n' +
          '           item\n' +
          '           variation\n' +
          '       }\n' +
          '       translations\n' +
          '       defaultLanguage\n' +
          '       showPricesWithCurrencyCode\n' +
          '       enableSubCategories\n' +
          '       packagingOptions\n' +
          '       payerInfoRequests\n' +
          '       allowedLanguages\n' +
          '       maintenanceMode\n' +
          '       collapseAllVariationGroups\n' +
          '       defaultSelectedItem\n' +
          '       skipBasketForDefaultItem\n' +
          '       upsellGroups {\n' +
          '           uuid\n' +
          '           label\n' +
          '           description\n' +
          '           sortingWeight\n' +
          '           status\n' +
          '           category {\n' +
          '               uuid\n' +
          '               name\n' +
          '               weight\n' +
          '               image {\n' +
          '                   file {\n' +
          '                       sources {\n' +
          '                           original\n' +
          '                       }\n' +
          '                   }\n' +
          '               }\n' +
          '               opening_hours {\n' +
          '                   day\n' +
          '                   starthours\n' +
          '                   endhours\n' +
          '               }\n' +
          '           }\n' +
          '      }\n' +
          '   }\n' +
          '}',
        variables: {
          uuid: kioskUUID,
        },
      }

      this.hasNetworkConnectionError = false
      this.hasNoKioskData = false

      this.getSystemTimeInMillis()
      return axios
        .post(this.graphUrl, body)
        .then((response) => {
          if (
            typeof response !== 'undefined' &&
            response !== null &&
            typeof response.data !== 'undefined' &&
            response.data !== null &&
            typeof response.data.data !== 'undefined' &&
            response.data.data !== null &&
            typeof response.data.data.kiosk !== 'undefined' &&
            response.data.data.kiosk !== null
          ) {
            this.kiosk = response.data.data.kiosk

            this.kiosk.items.sort((a, b) => (a.sorting_weight || 0) - (b.sorting_weight || 0))

            this.setDefaultSelectedLocation()
            this.kiosk.categories.sort((a, b) => a.weight - b.weight)

            if (this.kiosk.translations) {
              this.setTranslations(this.kiosk.translations)
            }

            this.setDefaultLanguage()
            window.salescloud_kiosk = this.kiosk

            // Add Kiosk information to Sentry context
            Sentry.setContext('kiosk', {
              kioskCode: this.kioskCode,
              kioskUuid: this.kiosk.uuid,
              kioskName: this.kiosk.title,
              organization: this.kiosk.organization?.uuid,
              organizationName: this.kiosk.organization?.label,
            })

            Sentry.setUser({
              id: this.kiosk.uuid,
              name: this.kiosk.organization?.label,
            })

            return this.kiosk
          }
          this.hasNoKioskData = true
          return null
        })
        .catch((err) => {
          this.hasNetworkConnectionError = true
          Sentry.captureException(err, { tags: { function: 'getKioskData' } })
          return null
        })
        .finally(() => {
          this.gettingInitialData = false
        })
    },
    reserveTimeSlot(item, slot, quantity) {
      const query = `
        mutation ReserveTimeSlot(
          $sessionToken: String!,
          $organization: String!,
          $item: String!,
          $startTimeInMillis: Float!,
          $endTimeInMillis: Float!,
          $quantity: Int!,
          $skipLock: Boolean!
        ) {
          reserveAvailableTimeSlot(
            sessionToken: $sessionToken,
            organization: $organization,
            item: $item,
            startTimeInMillis: $startTimeInMillis,
            endTimeInMillis: $endTimeInMillis,
            quantity: $quantity,
            skipLock: $skipLock
          ) {
            success
            uuid
          }
        }
      `

      const variables = {
        sessionToken: this.sessionToken,
        organization: this.kiosk.organization.uuid,
        item: item.uuid,
        startTimeInMillis: slot.timestampInMillis,
        endTimeInMillis: slot.timestampInMillis + slot.paddingInMillis,
        quantity,
        skipLock: item.reservationMode === 'relaxed',
      }

      const body = {
        query,
        variables,
      }

      const config = {
        headers: {
          'Salescloud-Organization': this.kiosk.organization.uuid,
        },
      }

      return axios
        .post(this.graphUrl, body, config)
        .then((result) => {
          if (result?.data?.data?.reserveAvailableTimeSlot) {
            return result.data.data.reserveAvailableTimeSlot
          }
          return null
        })
        .catch((e) => {
          Sentry.captureException(e, { tags: { function: 'reserveTimeSlot' } })
        })
    },
    releaseTimeSlot(item, slot) {
      const query = `
        mutation ReleaseTimeSlot(
          $sessionToken: String!,
          $organization: String!,
          $item: String!,
          $startTimeInMillis: Float!,
          $endTimeInMillis: Float!,
          $skipLock: Boolean!
        ) {
          releaseAvailableTimeSlot(
            sessionToken: $sessionToken,
            organization: $organization,
            item: $item,
            startTimeInMillis: $startTimeInMillis,
            endTimeInMillis: $endTimeInMillis,
            skipLock: $skipLock
          )
        }
      `

      const variables = {
        sessionToken: this.sessionToken,
        organization: this.kiosk.organization.uuid,
        item: item.uuid,
        startTimeInMillis: slot.timestampInMillis,
        endTimeInMillis: slot.timestampInMillis + slot.paddingInMillis,
        skipLock: item.reservationMode === 'relaxed',
      }

      const body = {
        query,
        variables,
      }

      const config = {
        headers: {
          'Salescloud-Organization': this.kiosk.organization.uuid,
        },
      }

      return axios
        .post(this.graphUrl, body, config)
        .then((result) => {
          if (result?.data?.data?.releaseAvailableTimeSlot) {
            return result.data.data.releaseAvailableTimeSlot
          }
          return null
        })
        .catch(() => {
          return null
        })
    },
    fetchSessionToken() {
      const graphOptions = {
        headers: {
          'Content-Type': 'application/json',
          'Salescloud-Organization': this.kiosk.organization.uuid,
        },
      }

      const graphBody = {
        query: 'mutation { newAnonymousSession { session_token, ttl } }',
      }

      return axios.post(this.graphUrl, graphBody, graphOptions).then((result) => {
        if (result && result.data && result.data.data && result.data.data.newAnonymousSession) {
          this.setSessionToken(result.data.data.newAnonymousSession.session_token)
        }
      })
    },
    async getAvailableTimeSlots(item) {
      const date = new Date()
      if (!item) {
        return []
      }

      const windowMinutes = this.kiosk && this.kiosk.availableTimeSlotsWindowInMinutes;
      const startTimeInMillis = new Date(date).setUTCHours(0, 0, 0, 0);
      const endTimeInMillis = windowMinutes > 0 ? date.getTime() + (windowMinutes * 60 * 1000) : new Date(date).setUTCHours(23, 59, 59, 999);

      if (!this.sessionToken) {
        await this.fetchSessionToken()
      }

      const body = {
        query:
          'query GetAvailableTimeSlots($sessionToken: String!, $organization: String!, $channel: String!, $location: String, $item: String, $startTimeInMillis: Float!, $endTimeInMillis: Float!) {\n' +
          '   availableTimeSlots(sessionToken: $sessionToken, organization: $organization, channel: $channel, location: $location, item: $item, startTimeInMillis: $startTimeInMillis, endTimeInMillis: $endTimeInMillis) {\n' +
          '       label\n' +
          '       timestampInMillis\n' +
          '       paddingInMillis\n' +
          '       reservedForCurrentSession\n' +
          '       reservationCount\n' +
          '       bookingCount\n' +
          '       availableCount\n' +
          '       isBlocked\n' +
          '   }\n' +
          '}',
        variables: {
          sessionToken: this.sessionToken,
          organization: this.kiosk.organization.uuid,
          channel: this.kiosk.channel.uuid,
          location: this.kiosk.locations[0].uuid,
          item: item.uuid,
          startTimeInMillis,
          endTimeInMillis,
        },
      }

      const config = {
        headers: {
          'Salescloud-Organization': this.kiosk.organization.uuid,
        },
      }

      return axios
        .post(this.graphUrl, body, config)
        .then((response) => {
          if (response && response.data && response.data.data && response.data.data.availableTimeSlots) {
            return response.data.data.availableTimeSlots
          }
          return []
        })
        .catch(() => {
          return []
        })
    },
    getDisplayScaledNumber(scale, fontSize) {
      return fontSize * scale
    },
    getDisplayScale(displayWidth) {
      const designedForWidth = 1100

      if (displayWidth < designedForWidth) {
        return displayWidth / designedForWidth
      }

      return 1
    },
    getPreviewData() {
      // We really should just make graph parse through the preview data
      if (!this.previewValues) {
        return null
      }

      this.gettingInitialData = true

      // The setup splash screen gates on a kiosk being fetched by uuid. A preview is
      // assembled from the values the admin is editing, so there is nothing to set up
      // and nothing to fetch by uuid - close it explicitly.
      this.showInitialSetupScreen = false

      if (!this.kiosk) {
        this.kiosk = {}
      }

      const previewOrganization = this.previewValues.organization
      const previewChannel = this.previewValues.channel
      const previewCategories = this.previewValues.categories
      const previewLocations = this.previewValues.locations

      const previewDefaultLanguage = this.previewValues.defaultLanguage
      if (previewDefaultLanguage) {
        this.kiosk.defaultLanguage = previewDefaultLanguage
        this.setDefaultLanguage()
      }
      const previewShowPricesWithCurrencyCode = this.previewValues.showPricesWithCurrencyCode
      if (typeof previewShowPricesWithCurrencyCode === 'boolean') {
        this.kiosk.showPricesWithCurrencyCode = previewShowPricesWithCurrencyCode
      }
      const previewEnableSubCategories = this.previewValues.enableSubCategories
      if (typeof previewEnableSubCategories === 'boolean') {
        this.kiosk.enableSubCategories = previewEnableSubCategories
      }
      const previewPackagingOptions = this.previewValues.packagingOptions
      if (previewPackagingOptions) {
        this.kiosk.packagingOptions = previewPackagingOptions
      }
      const previewPayerInfoRequests = this.previewValues.payerInfoRequests
      if (previewPayerInfoRequests) {
        this.kiosk.payerInfoRequests = previewPayerInfoRequests
      }

      const previewAllowedLanguages = this.previewValues.allowedLanguages
      if (previewAllowedLanguages) {
        this.kiosk.allowedLanguages = previewAllowedLanguages
      }

      const previewMaintenanceMode = this.previewValues.maintenanceMode
      if (typeof previewMaintenanceMode === 'boolean') {
        this.kiosk.maintenanceMode = previewMaintenanceMode
      }

      const previewOpeningHours = this.previewValues.openingHours
      if (previewOpeningHours) {
        this.kiosk.openingHours = previewOpeningHours
      }

      if (typeof this.previewValues.active === 'boolean') {
        this.kiosk.active = this.previewValues.active
      }

      if (typeof this.previewValues.isOpen === 'boolean') {
        this.kiosk.isOpen = this.previewValues.isOpen
      }

      // Settings the admin can edit that only show up once the customer is past the front
      // page. They arrive in previewValues like everything else, so a preview that does not
      // copy them silently renders as though the toggle were off, whatever the form says.
      const previewDefaultSelectedItem = this.previewValues.defaultSelectedItem
      if (previewDefaultSelectedItem) {
        this.kiosk.defaultSelectedItem = previewDefaultSelectedItem
      }

      const previewSkipBasketForDefaultItem = this.previewValues.skipBasketForDefaultItem
      if (typeof previewSkipBasketForDefaultItem === 'boolean') {
        this.kiosk.skipBasketForDefaultItem = previewSkipBasketForDefaultItem
      }

      const previewCollapseAllVariationGroups = this.previewValues.collapseAllVariationGroups
      if (typeof previewCollapseAllVariationGroups === 'boolean') {
        this.kiosk.collapseAllVariationGroups = previewCollapseAllVariationGroups
      }

      const previewAllowDifferentPaymentMethods = this.previewValues.allowDifferentPaymentMethods
      if (typeof previewAllowDifferentPaymentMethods === 'boolean') {
        this.kiosk.allowDifferentPaymentMethods = previewAllowDifferentPaymentMethods
      }

      const previewAvailableTimeSlotsWindowInMinutes = this.previewValues.availableTimeSlotsWindowInMinutes
      if (!isNaN(Number(previewAvailableTimeSlotsWindowInMinutes)) && previewAvailableTimeSlotsWindowInMinutes !== null) {
        this.kiosk.availableTimeSlotsWindowInMinutes = Number(previewAvailableTimeSlotsWindowInMinutes)
      }

      const body = {
        query:
          'query PreviewQuery($organization: String!, $channel: String!, $locations: [String!]) {\n' +
          '   organization(uuid: $organization) {\n' +
          '       uuid\n' +
          '       label\n' +
          '       phone\n' +
          '       email\n' +
          '       ssn\n' +
          '       description\n' +
          '       primary_color\n' +
          '       accent_color\n' +
          '       address {\n' +
          '           name_line\n' +
          '           thoroughfare\n' +
          '           locality\n' +
          '           country\n' +
          '           postal_code' +
          '       }\n' +
          '       settings {\n' +
          '           booleans {\n' +
          '               key\n' +
          '               value\n' +
          '           }\n' +
          '       }\n' +
          '       defaultCurrency {\n' +
          '           code\n' +
          '           name\n' +
          '       }\n' +
          '       defaultTimezone\n' +
          '       logo {\n' +
          '           file {\n' +
          '               sources {\n' +
          '                   original\n' +
          '                   large\n' +
          '                   medium\n' +
          '                   thumbnail\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '   }\n' +
          '   channel(organization: $organization, uuid: $channel) {\n' +
          '       uuid\n' +
          '       label\n' +
          '       ept\n' +
          '       data\n' +
          '       acceptedPaymentMethodInstances {\n' +
          '           uuid\n' +
          '           title\n' +
          '           namespace\n' +
          '           icon\n' +
          '           schemeIcon\n' +
          '           isApplePayCapable\n' +
          '           weight\n' +
          '           terminal\n' +
          '           checkout\n' +
          '           uiFields {\n' +
          '               key\n' +
          '               type\n' +
          '               label\n' +
          '               description\n' +
          '               required\n' +
          '               value\n' +
          '               cols\n' +
          '               placeholder\n' +
          '               hint\n' +
          '               options {\n' +
          '                   key\n' +
          '                   label\n' +
          '               }\n' +
          '               multiple\n' +
          '               minLength\n' +
          '               maxLength\n' +
          '               weight\n' +
          '           }\n' +
          '       }\n' +
          '   }\n' +
          '   locationOptions(organization: $organization) {\n' +
          '       uuid\n' +
          '       label\n' +
          '       ept\n' +
          '       description\n' +
          '       address {\n' +
          '           name_line\n' +
          '           country\n' +
          '           postal_code\n' +
          '           thoroughfare\n' +
          '           locality\n' +
          '       }\n' +
          '       timezone\n' +
          '       isOpen\n' +
          '       preferredChannel {\n' +
          '           uuid\n' +
          '           label\n' +
          '           ept\n' +
          '           data\n' +
          '           acceptedPaymentMethodInstances {\n' +
          '               uuid\n' +
          '               title\n' +
          '               namespace\n' +
          '               icon\n' +
          '               schemeIcon\n' +
          '               isApplePayCapable\n' +
          '               weight\n' +
          '               terminal\n' +
          '               checkout\n' +
          '               uiFields {\n' +
          '                   key\n' +
          '                   type\n' +
          '                   label\n' +
          '                   description\n' +
          '                   required\n' +
          '                   value\n' +
          '                   cols\n' +
          '                   placeholder\n' +
          '                   hint\n' +
          '                   options {\n' +
          '                       key\n' +
          '                       label\n' +
          '                   }\n' +
          '                   multiple\n' +
          '                   minLength\n' +
          '                   maxLength\n' +
          '                   weight\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '   }\n' +
          '   itemsList(organization: $organization, sellOnline: true, locations: $locations) {\n' +
          '       uuid\n' +
          '       title\n' +
          '       sorting_weight\n' +
          '       exclude_discounts\n' +
          '       reservationMode\n' +
          '       type\n' +
          '       description {\n' +
          '           value\n' +
          '       }\n' +
          '       categories {\n' +
          '           uuid\n' +
          '           name\n' +
          '           description\n' +
          '           weight\n' +
          '           parent {\n' +
          '               uuid\n' +
          '               name\n' +
          '           }\n' +
          '           image {\n' +
          '             file {\n' +
          '               sources {\n' +
          '                 original\n' +
          '                 large\n' +
          '                 medium\n' +
          '                 thumbnail\n' +
          '               }\n' +
          '             }\n' +
          '           }\n' +
          '       }\n' +
          '       price {\n' +
          '           amount\n' +
          '           currency_code\n' +
          '       }\n' +
          '       sale_price {\n' +
          '           amount\n' +
          '           currency_code\n' +
          '       }\n' +
          '       images {\n' +
          '           file {\n' +
          '               sources {\n' +
          '                   original\n' +
          '                   large\n' +
          '               }\n' +
          '           }\n' +
          '       }\n' +
          '       default_variations {\n' +
          '           uuid\n' +
          '           label\n' +
          '           attribute {\n' +
          '               uuid\n' +
          '               name\n' +
          '               description\n' +
          '               weight\n' +
          '               required\n' +
          '               single\n' +
          '               multiple\n' +
          '               maxVariations\n' +
          '           }\n' +
          '           sorting_weight\n' +
          '           price {\n' +
          '               amount\n' +
          '               currency_code\n' +
          '           }\n' +
          '       }\n' +
          '       variations {\n' +
          '           uuid\n' +
          '           label\n' +
          '               images {\n' +
          '                   file {\n' +
          '                     sources {\n' +
          '                       large\n' +
          '                     }\n' +
          '                   }\n' +
          '               }\n' +
          '           attribute {\n' +
          '               uuid\n' +
          '               name\n' +
          '               description\n' +
          '               weight\n' +
          '               required\n' +
          '               single\n' +
          '               multiple\n' +
          '               maxVariations\n' +
          '           }\n' +
          '           sorting_weight\n' +
          '           price {\n' +
          '               amount\n' +
          '               currency_code\n' +
          '           }\n' +
          '       }\n' +
          '       sellInKiosk\n' +
          '       sellInLocations {\n' +
          '           uuid\n' +
          '           label\n' +
          '       }\n' +
          '       ' +
          '   }\n' +
          '   organizationTranslationsI18nFormatted(organization: $organization) \n' +
          '   itemLocationBlocks(organization: $organization) {\n' +
          '       location\n' +
          '       item\n' +
          '   }\n' +
          '   variationLocationBlocks(organization: $organization) {\n' +
          '       location\n' +
          '       item\n' +
          '       variation\n' +
          '   }\n' +
          '}',
        variables: {
          organization: previewOrganization,
          channel: previewChannel,
          locations: previewLocations,
        },
      }

      this.getSystemTimeInMillis()
      return axios
        .post(this.graphUrl, body)
        .then((response) => {
          if (!response?.data?.data) {
            return null
          }
          const {
            organization,
            channel,
            locationOptions,
            itemsList,
            organizationTranslationsI18nFormatted,
            itemLocationBlocks,
            variationLocationBlocks,
          } = response.data.data

          if (organization) {
            this.kiosk.organization = organization
          }

          if (channel) {
            this.kiosk.channel = channel
          }

          if (Array.isArray(locationOptions)) {
            if (Array.isArray(previewLocations) && previewLocations.length > 0) {
              this.kiosk.locations = locationOptions.filter((location) => location && previewLocations.includes(location.uuid))
            } else {
              this.kiosk.locations = locationOptions
            }

            this.setDefaultSelectedLocation()

            // setDefaultSelectedLocation only picks a location when the kiosk has exactly one,
            // and a preview with no locations chosen falls back to every location the
            // organization has. Left unselected, the packaging screen never opens and the
            // preview drops straight onto the item list, so settle on the first location to
            // show the flow a real customer gets. Previews are not real sessions, so which
            // one it lands on only matters for location prices and blocks.
            if (!this.order) {
              this.order = {}
            }
            if (!this.order.selectedLocation && this.kiosk.locations.length > 0) {
              this.order.selectedLocation = this.kiosk.locations[0]
            }
          }

          if (itemsList) {
            itemsList.sort((a, b) => (a.sorting_weight || 0) - (b.sorting_weight || 0))

            this.kiosk.items = itemsList
            if (previewCategories) {
              for (let i = 0; i < itemsList.length; i++) {
                const item = itemsList[i]
                if (item && Array.isArray(item.categories)) {
                  for (let categoryIndex = 0; categoryIndex < item.categories.length; categoryIndex++) {
                    const category = item.categories[categoryIndex]
                    if (category && previewCategories.includes(category.uuid)) {
                      if (!this.kiosk.categories) {
                        this.kiosk.categories = []
                      }
                      if (this.kiosk.categories.some((c) => c && c.uuid === category.uuid)) {
                        continue
                      }
                      this.kiosk.categories.push(category)
                    }
                  }
                }
              }
            }
          }
          if (Array.isArray(this.kiosk.categories)) {
            this.kiosk.categories.sort((a, b) => a.weight - b.weight)
          }

          if (organizationTranslationsI18nFormatted) {
            this.setTranslations(organizationTranslationsI18nFormatted)
          }

          if (itemLocationBlocks) {
            this.kiosk.itemLocationBlocks = itemLocationBlocks
          }

          if (variationLocationBlocks) {
            this.kiosk.variationLocationBlocks = variationLocationBlocks
          }

          return response.data.data
        })
        .catch((err) => {
          Sentry.captureException(err, { tags: { function: 'getPreviewData' } })
          return null
        })
        .finally(() => {
          this.gettingInitialData = false
        })
    },
    async addToOrder(item, quantity, selectedVariations, selectedTimeSlot) {
      // Anything added after the reader has been given an amount is billed but never
      // charged, so keep the cart still for the duration
      if (this.cardReaderTransactionInFlight) {
        this.customLogger('info', 'Ignoring addToOrder while a card reader transaction is in flight', {
          itemUuid: item?.uuid,
        })
        return
      }

      if (!this.order) {
        this.order = {}
      }
      if (!Array.isArray(this.order.lineItems)) {
        this.order.lineItems = []
      }

      let addedSuccessfully = false
      const itemUuid = item?.uuid

      try {
        if (item.type === 'event' && selectedTimeSlot) {
          const hasMultipleVariations = selectedVariations.length > 1

          await this.reserveTimeSlot(item, selectedTimeSlot, hasMultipleVariations ? selectedVariations.length : quantity)
        }

        const tempItem = JSON.parse(JSON.stringify(item))
        tempItem.quantity = quantity
        tempItem.selectedVariations = selectedVariations
        tempItem.selectedTimeSlot = selectedTimeSlot
        tempItem.totalPrice = calculateItemCurrentPrice(tempItem, quantity, selectedVariations)
        this.order.lineItems.push(tempItem)
        addedSuccessfully = true
      } catch (e) {
        // Release the timeslot if we failed to add to order
        if (item?.type === 'event' && selectedTimeSlot) {
          await this.releaseTimeSlot(item, selectedTimeSlot)
        }
      }

      // Default-product shortcut: when the kiosk has a configured default product
      // and the item just added is that product, skip the catalog/cart review and
      // jump straight into the checkout flow. Other products keep today's behaviour.
      //
      // skipBasketForDefaultItem is a *separate* toggle from defaultSelectedItem: it only
      // controls whether the basket is skipped, not whether the item is auto-opened on
      // startup. Existing kiosk configs predate this field and store it as undefined/null
      // in MongoDB, so the check must be an explicit `!== false` (not a truthy check) -
      // absent/null must keep meaning "skip the basket" to preserve today's behaviour for
      // every kiosk already relying on it. Only an explicit `false` restores the basket.
      if (
        addedSuccessfully &&
        itemUuid &&
        itemUuid === this.kiosk?.defaultSelectedItem &&
        this.kiosk?.skipBasketForDefaultItem !== false
      ) {
        this.enableCheckout()
      }
    },
    async removeFromOrder(lineItemIndex) {
      // Same reason as addToOrder: removing after the reader has been given an amount
      // leaves the customer overpaying for an order that no longer matches
      if (this.cardReaderTransactionInFlight) {
        this.customLogger('info', 'Ignoring removeFromOrder while a card reader transaction is in flight', {
          lineItemIndex,
        })
        return
      }

      try {
        // Get the item before removing it
        const lineItem = this.order.lineItems[lineItemIndex]

        // If it's an event with a selected timeslot, release it
        if (lineItem?.type === 'event' && lineItem?.selectedTimeSlot) {
          await this.releaseTimeSlot(lineItem, lineItem.selectedTimeSlot)
        }

        // Existing logic
        this.order.lineItems.splice(lineItemIndex, 1)
      } catch (e) {
        //
      }
    },
    async dnsOverHttps(domain) {
      try {
        const dohProvider = 'https://cloudflare-dns.com/dns-query'
        const responseType = 'application/dns-json'

        const response = await axios.get(dohProvider, {
          params: {
            name: domain,
            type: 'A',
          },
          headers: {
            Accept: responseType,
          },
        })

        const ipAddresses = response.data.Answer.map((answer) => answer.data)
        return ipAddresses
      } catch (error) {
        Sentry.captureException(error, { extra: 'dnsOverHttps' })
        console.error('Error resolving domain:', error)
        return []
      }
    },
    // Add this helper method near the top of the actions
    logIdempotencyStatus(response, request, context) {
      try {
        // Log idempotency status if present
        const idempotencyStatus = response.headers['x-idempotency-status']
        if (idempotencyStatus) {
          Sentry.addBreadcrumb({
            category: 'process',
            message: 'Idempotency status received',
            level: 'info',
            data: {
              status: idempotencyStatus,
              entangleKey: request.entangleKey,
              requestId: request.requestId,
              ...context,
            },
          })
        }
      } catch (error) {
        console.error('Error capturing idempotency status', error)
      }
    },
    async doPostRequest(job) {
      const { id, domain, path, data, headers, retries, retryDelay } = job

      try {
        const ipAddresses = await this.dnsOverHttps(domain)

        if (!ipAddresses.length > 1) {
          throw new Error(`No IP addresses found for ${domain}`)
        }

        if (ipAddresses[0].endsWith('.')) {
          ipAddresses[0] = ipAddresses[0].slice(0, -1)
        }

        if (retries > 0) {
          Sentry.addBreadcrumb({
            category: 'process',
            message: `Retrying ${retries} times`,
            level: 'info',
            data: { entangleKey: data.entangleKey },
          })
        }

        const targetUrl = `https://${domain}${path}`
        const response = await axios.post(targetUrl, data, {
          headers: {
            ...headers,
          },
        })

        Sentry.addBreadcrumb({
          category: 'process',
          message: 'Sending card reader payment to api',
          level: 'info',
          data,
        })
        if (response.data !== null && response.data !== undefined) {
          this.paymentResponse = response.data
          this.originalPaymentResponse = response.data
          this.customLogger('info', 'POST request successful', response.data)
          this.currentlyProcessingPaymentTransactionId = null
          this.removeRetryJob(id)

          try {
            Sentry.addBreadcrumb({
              category: 'process',
              message: 'Response received from payment service',
              data: response.data,
            })
            Sentry.addBreadcrumb({
              category: 'process',
              message: 'Payment Made',
              level: 'info',
            })
            this.logIdempotencyStatus(response, data, { domain, path })
          } catch (error) {
            console.error('Error capturing payment message', error)
          }
        }
      } catch (error) {
        // Handle 409 Conflict (Idempotency conflict)
        if (error?.response?.status === 409) {
          Sentry.captureMessage('Idempotency conflict detected', {
            level: 'warning',
            extra: {
              entangleKey: data.entangleKey,
              requestId: data.requestId,
              domain,
              path,
            },
          })
        }

        Sentry.captureException(error, { extra: 'doPostRequest' })
        this.customLogger('error', 'Error making POST request:', error)

        if (retries > 0) {
          this.customLogger('info', `Retrying in ${retryDelay}ms...`)

          const updatedJob = {
            ...job,
            retries: retries - 1,
            retryDelay: retryDelay * 2,
          }

          // Save the updated job to localStorage
          this.removeRetryJob(id)
          this.setRetryJobs([...this.getRetryJobs(), updatedJob])

          // Schedule the retry
          setTimeout(() => this.doPostRequest(updatedJob), retryDelay)
        } else {
          this.customLogger('error', 'No more retries left. Aborting.')
          this.removeRetryJob(id)

          // Only clear currentlyProcessingPaymentTransactionId when no retries are left
          this.currentlyProcessingPaymentTransactionId = null
        }
      }
    },
    setRetryJobs(jobs) {
      localStorage.setItem('retryJobs', JSON.stringify(jobs))
    },
    getRetryJobs() {
      const jobs = localStorage.getItem('retryJobs')
      return jobs ? JSON.parse(jobs) : []
    },
    removeRetryJob(jobId) {
      const jobs = this.getRetryJobs()
      const updatedJobs = jobs.filter((job) => job.id !== jobId)
      this.setRetryJobs(updatedJobs)
    },
    addPostRequestJob(domain, path, data, headers, retries = 3, retryDelay = 3000) {
      const jobId = `${Date.now()}-${Math.random().toString(36).substr(2, 8)}`

      const job = {
        id: jobId,
        domain,
        path,
        data,
        headers,
        retries,
        retryDelay,
      }

      this.setRetryJobs([...this.getRetryJobs(), job])

      // Execute the job immediately
      this.doPostRequest(job)
    },
    processPendingRetryJobs() {
      const retryJobs = this.getRetryJobs()
      retryJobs.forEach((job) => this.doPostRequest(job))
    },
    getCustomerDiscount(ssn) {
      if (!ssn) {
        return Promise.resolve(0)
      }

      return apolloClient
        .query({
          query: GET_CUSTOMER_DISCOUNT,
          variables: {
            organization: this.kiosk.organization.uuid,
            ssn: ssn,
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          if (response?.data?.customerDiscount) {
            return response.data.customerDiscount
          }
          return 0
        })
        .catch((error) => {
          console.error('Error getting customer discount', error)
          return 0
        })
    },
    applyDiscountToLineItems(lineItems, discountPercent) {
      if (!discountPercent || discountPercent <= 0 || !Array.isArray(lineItems)) {
        return lineItems
      }

      return lineItems.map((lineItem) => {
        const updatedLineItem = { ...lineItem }

        // Add the discount percentage to each line item
        updatedLineItem.discountPercent = discountPercent

        return updatedLineItem
      })
    },
    async processPayment(paymentRequest) {
      const url = paymentRequest.url
      const request = paymentRequest.object

      const headers = { 'X-Request-Key': request.requestId ?? uuidV4() }

      if (this.kiosk.enableIdempotentPaymentRequests) {
        const entangleKey = `${uuidV4()}:${Date.now() / 1000}`
        request.entangleKey = entangleKey
        headers['x-idempotency'] = 'true'
      }

      const isInvoicePayment = request.namespace === 'invoice'
      if (isInvoicePayment) {
        try {
          const customerSSN = request.details?.customerUUID
          if (customerSSN) {
            const discountPercent = await this.getCustomerDiscount(customerSSN)
            if (discountPercent > 0 && Array.isArray(request.items)) {
              request.items = this.applyDiscountToLineItems(request.items, discountPercent)
              request.amount = this.calculateCartTotal(request.items)
            }
          }
        } catch (error) {
          console.error('Error processing invoice payment:', error)
        }
      }

      return axios
        .post(url, request, { headers })
        .then((response) => {
          if (response?.data) {
            this.paymentResponse = response.data
            this.originalPaymentResponse = response.data

            try {
              Sentry.addBreadcrumb({
                category: 'process',
                message: 'Response received from payment service',
                data: response.data,
              })
              Sentry.addBreadcrumb({
                category: 'process',
                message: 'Payment Made',
                level: 'info',
              })
              this.logIdempotencyStatus(response, request, { url })
            } catch (error) {
              console.error('Error capturing payment message', error)
            }

            return response.data
          }
          // Handle response values
          return null
        })
        .catch((error) => {
          // Handle 409 Conflict (Idempotency conflict)
          if (error?.response?.status === 409) {
            Sentry.captureMessage('Idempotency conflict detected', {
              level: 'warning',
              extra: {
                entangleKey: request.entangleKey,
                requestId: request.requestId,
                url,
              },
            })
          }

          // Handle fault values
          if (error?.response?.status && error?.response?.data) {
            console.error('[STORE][PAYMENTREQUEST] http status', error.response.status, error.response.statusText)
            console.error('[STORE][PAYMENTREQUEST] data', error.response.data)

            this.paymentResponse = error.response.data
            this.originalPaymentResponse = error.response.data
            return error.response.data
          }

          return null
        })
    },
    printReceipt(paymentResponse) {
      // Create receipt html
      // organization, location, kiosk, deviceInfo, paymentRequest, paymentResponse
      try {
        if (this.kiosk.receiptPrinter === null || this.kiosk.receiptPrinter === undefined) {
          return false
        }

        if (paymentResponse.fiscalReceipt === null || paymentResponse.fiscalReceipt === undefined) {
          return false
        }

        const html = paymentResponse.fiscalReceipt

        const printRequest = {
          htmlString: html,
          ipAddress: this.kiosk.receiptPrinter.ipAddress,
          width: 385,
        }

        switch (this.kiosk.receiptPrinter.model) {
          case 'star_mcp_2':
            printRequest.width = 385
            break
          case 'star_mcp_3':
            printRequest.width = 576
            break
          default:
            printRequest.width = 385
            break
        }

        StarPrinterConnect.print(printRequest)
      } catch (e) {
        Sentry.captureException(e, { extra: 'printReceipt' })
      }

      // Convert html to image
      // Send image to printer

      return false
    },
    // This function is used to check the printer status
    // If there is an active order, we don't need to check the printer status
    async updatePrinterStatus() {
      try {
        if (!this.kiosk || this.kiosk.receiptPrinter === null || this.kiosk.receiptPrinter === undefined) {
          this.hasPrinterPaper = true
          return
        }

        if (this.order && this.order.lineItems) {
          return
        }

        // The following can be added to the options to debug the printer status
        //  , debug: { receiptPaperEmpty: false }
        const options = { ipAddress: this.kiosk.receiptPrinter.ipAddress }

        const result = await StarPrinterConnect.checkStatus(options)

        this.hasPrinterPaper = !result.status.receiptPaperEmpty
      } catch (e) {
        if (e.code === 'UNIMPLEMENTED') {
          // Checking printer status is not implemented on web
          return
        }

        Sentry.captureException(e, { extra: 'checkStatus' })
      }
    },
    async checkPendingPayment(url, namespace = null) {
      await delay(defaultDelayTime)

      // Modify URL for specific payment methods
      let requestUrl = url
      if (CLOUD_TERMINAL_NAMESPACES.includes(namespace)) {
        requestUrl = `${url}/resolve`
      }

      return axios
        .get(requestUrl)
        .then((response) => {
          if (response?.data) {
            this.paymentResponse = response.data
            return response.data
          }
          // Handle response values
          return null
        })
        .catch(() => {
          // Handle fault values
          return null
        })
    },
    setDefaultLanguage() {
      let defaultLanguage = 'en'

      if (typeof this.kiosk.defaultLanguage !== 'undefined' && this.kiosk.defaultLanguage !== null) {
        if (i18n.global.availableLocales.includes(this.kiosk.defaultLanguage)) {
          defaultLanguage = this.kiosk.defaultLanguage
        }
      }

      if (i18n.mode === 'legacy') {
        i18n.global.locale = defaultLanguage
      } else {
        i18n.global.locale.value = defaultLanguage
      }
    },
    reset() {
      this.customLogger('info', 'Resetting Kiosk')

      // Release all timeslots in the cart
      if (this.order?.lineItems) {
        for (const lineItem of this.order.lineItems) {
          if (lineItem.type === 'event' && lineItem.selectedTimeSlot) {
            this.releaseTimeSlot(lineItem, lineItem.selectedTimeSlot)
          }
        }
      }

      // Reset order and set default location if only one exists
      this.order = {}
      if (Array.isArray(this.kiosk.locations) && this.kiosk.locations.length === 1) {
        this.order.selectedLocation = this.kiosk.locations[0]
      }

      // Reset payment related states
      this.paymentResponse = null
      this.originalPaymentResponse = null
      this.clearLastProcessedPayment()

      // Reset UI states
      this.showConfirmActivity = false
      this.showPaymentOptions = false
      this.showPaymentMethodInput = false
      this.checkoutInProgress = false
      this.startPaymentDirectly = false
      this.cardReaderTransactionInFlight = false

      // Reset coupon state
      this.showCouponDialog = false
      this.couponCode = ''
      this.couponCalculation = null
      this.couponValidating = false
      this.couponError = null

      // Reset language and session
      this.setDefaultLanguage()
      this.setSessionToken(null)

      // Next customer starts from the front page again — packaging selection
      // will trigger autoSelectDefaultItemIfConfigured() for the configured product.
      this.selectedItem = null
    },
    setDefaultSelectedLocation() {
      if (Array.isArray(this.kiosk.locations) && this.kiosk.locations.length === 1) {
        if (!this.order) {
          this.order = {}
        }
        if (!this.order.selectedLocation) {
          this.order.selectedLocation = this.kiosk.locations[0]
        }
      }
    },
    setTranslations(translations) {
      const keys = Object.keys(translations)

      for (let i = 0; i < keys.length; i++) {
        i18n.global.mergeLocaleMessage(keys[i], translations[keys[i]])
      }
    },
    async getSystemTimeInMillis() {
      const body = { query: 'query { systemTimeInMillis }' }

      const systemTimeInMillis = await axios
        .post(this.graphUrl, body)
        .then((response) => {
          if (!response?.data?.data?.systemTimeInMillis) {
            return null
          }
          return response.data.data.systemTimeInMillis
        })
        .catch(() => {
          return null
        })

      // If we get no value, we use the current time from DateTime as fallback
      const timezone = this.kiosk?.openingHours?.timezone ?? 'UTC'
      this.systemTimeInMillis = systemTimeInMillis ?? DateTime.local({ zone: timezone }).toMillis()
      return systemTimeInMillis
    },
    parseDateTimeFromHours(hours, dateTimeValue, timezone) {
      // Test the timezone, if it's invalid use UTC
      if (!DateTime.local().setZone(timezone).isValid) {
        timezone = 'UTC'
      }

      // When dateTimeValue is given an invalid value, we set it to null
      if (!dateTimeValue?.isValid) {
        dateTimeValue = undefined
      }

      // We need the correct value for selected time, so resulting DateTime object returns the correct value
      let dateTime = dateTimeValue?.setZone(timezone) || DateTime.local({ zone: timezone })

      // Handle undefined or null values
      if (!hours) {
        return dateTime
      }

      // Determine the format of the hours
      let prunedHours = hours.replace(/[:\s]/g, '')

      // Handle invalid hours
      if (!Number(prunedHours)) {
        return dateTime
      }

      // Assumes hours to be '5' rather than '05' meaning '0500'
      if (prunedHours.length === 1) {
        prunedHours = `0${prunedHours}00`
      }

      // Assumes hours to be '15' rather than '1500'
      if (prunedHours.length === 2 || prunedHours.length === 0) {
        prunedHours = prunedHours.padEnd(4, '0')
      }

      // Assumes hours to be '500' rather than '0500'
      if (prunedHours.length === 3) {
        prunedHours = `0${prunedHours}`
      }

      // Parse the hours into hours and minutes
      const hour = Number(prunedHours.substring(0, 2))
      const minute = Number(prunedHours.substring(2, 4))

      // Set the DateTime object with the parsed hours and minutes, along with timezone
      dateTime = dateTime.set({ hour, minute, second: 0, millisecond: 0 })

      // Return the DateTime object
      return dateTime
    },
    checkIfClosedDay(closedDays, currentTime) {
      if (!Array.isArray(closedDays)) {
        return false
      }

      return closedDays.includes(currentTime.toFormat('yyyy-MM-dd'))
    },
    checkIfInSeasonalHours(seasonalHours, currentTime, timezone) {
      if (!Array.isArray(seasonalHours)) {
        return { isInSeasonalHours: false, isInSeasonalDay: false }
      }

      if (!currentTime) {
        currentTime = DateTime.local({ zone: timezone })
      }

      const currentStartOfPeriod = currentTime.startOf('day').toMillis()
      const currentEndOfPeriod = currentTime.endOf('day').toMillis()
      const currentTimeInMillis = currentTime.toMillis()

      let isInSeasonalDay = false
      let isInSeasonalHours = false

      for (const seasonalHour of seasonalHours) {
        if (!seasonalHour) {
          continue
        }

        let startTimeInMillis = seasonalHour.startTimeInMillis ?? 0
        let endTimeInMillis = seasonalHour.endTimeInMillis ?? 0

        if (startTimeInMillis <= 0 || endTimeInMillis <= 0) {
          continue
        }

        // Within seasonal period
        if (startTimeInMillis >= currentStartOfPeriod && endTimeInMillis <= currentEndOfPeriod) {
          isInSeasonalDay = true
        }

        // Handling seasonal hour spanning multiple days
        const startTime = DateTime.fromMillis(startTimeInMillis)
        const endTime = DateTime.fromMillis(endTimeInMillis)
        if (isInSeasonalDay && !startTime.hasSame(endTime, 'day')) {
          const startHour = this.parseDateTimeFromHours(seasonalHour.starthours, currentTime, timezone)
          const endHour = this.parseDateTimeFromHours(seasonalHour.endhours, currentTime, timezone)

          startTimeInMillis = startHour.toMillis()
          endTimeInMillis = endHour.toMillis()

          // Handling beyond midnight opening hours
          const endHoursBeyondMidnight = endHour.hour < startHour.hour
          if (endHoursBeyondMidnight) {
            const currentTimeAfterMidnight = currentTime.hour < startHour.hour
            const currentTimeBeforeMidnight = currentTime.hour > endHour.hour
            if (currentTimeAfterMidnight) {
              startTimeInMillis = startHour.minus({ days: 1 }).toMillis()
            }
            if (currentTimeBeforeMidnight) {
              endTimeInMillis = endTime.plus({ days: 1 }).toMillis()
            }
          }
        }
        // Needs to handle start of day of seasonal period
        // Needs to handle end of day of seasonal period
        // Needs to handle middle of seasonal period

        if (isInSeasonalDay && currentTimeInMillis >= startTimeInMillis && currentTimeInMillis <= endTimeInMillis) {
          isInSeasonalHours = true
        }
      }

      return { isInSeasonalHours, isInSeasonalDay }
    },
    checkIfInWeekdayHours(weekdayHours, currentTime, timezone) {
      if (!Array.isArray(weekdayHours)) {
        return false
      }

      if (!currentTime) {
        currentTime = DateTime.local({ zone: timezone })
      }

      const currentWeekday = currentTime.weekday
      const currentTimeInMillis = currentTime.toMillis()
      for (const weekdayHour of weekdayHours) {
        if (!weekdayHour) {
          continue
        }

        if (typeof weekdayHour.day !== 'string') {
          continue
        }

        let weekday = Number(weekdayHour.day)
        if (isNaN(weekday)) {
          continue
        }

        if (weekday === 0) {
          weekday = 7
        }

        let startHours = this.parseDateTimeFromHours(weekdayHour.starthours, currentTime, timezone)
        let endHours = this.parseDateTimeFromHours(weekdayHour.endhours, currentTime, timezone)

        if (startHours === endHours) {
          continue
        }

        // Handle beyond midnight opening hours
        if (endHours < startHours) {
          const currentTimeBeforeMidnight = currentTime.hour > startHours.hour
          const currentTimeAfterMidnight = currentTime.hour < endHours.hour
          if (currentTimeBeforeMidnight) {
            endHours = endHours.plus({ days: 1 })
          }
          if (currentTimeAfterMidnight) {
            startHours = startHours.minus({ days: 1 })
            weekday = +1
            if (weekday > 7) {
              weekday = 1
            }
          }
        }

        if (weekday !== currentWeekday) {
          continue
        }

        if (currentTimeInMillis >= startHours.toMillis() && currentTimeInMillis <= endHours.toMillis()) {
          return true
        }
      }

      return false
    },
    checkKioskOpenStatus() {
      const currentTimeInMillis = this.systemTimeInMillis
      const currentTime = DateTime.fromMillis(currentTimeInMillis)

      // No need to check opening hours if the kiosk is not active
      if (!this.kiosk || this.kiosk.active === false) {
        return
      }

      if (!this.hasPrinterPaper) {
        this.kiosk.isOpen = false
        return
      }

      const kioskOpeningHours = this.kiosk.openingHours ?? null
      if (!kioskOpeningHours) {
        this.kiosk.isOpen = true
        return
      }

      if (kioskOpeningHours.enabled === false) {
        this.kiosk.isOpen = true
        return
      }

      const weekdayHours = kioskOpeningHours.weekdayHours ?? null
      const seasonalHours = kioskOpeningHours.seasonalHours ?? null
      const closedDays = kioskOpeningHours.closedDates ?? null
      const timezone = kioskOpeningHours.timezone ?? null

      // Checking if today is a closed day
      const isClosedDay = this.checkIfClosedDay(closedDays, currentTime)

      if (isClosedDay) {
        this.kiosk.isOpen = false
        return
      }

      // Check for seasonalHours
      const { isInSeasonalHours, isInSeasonalDay } = this.checkIfInSeasonalHours(seasonalHours, currentTime, timezone)

      if (isInSeasonalHours) {
        this.kiosk.isOpen = true
        return
      } else if (isInSeasonalDay && !isInSeasonalHours) {
        this.kiosk.isOpen = false
        return
      }

      // Check for weekdayHours
      this.kiosk.isOpen = this.checkIfInWeekdayHours(weekdayHours, currentTime, timezone)
    },
    normalizeWeekday(weekday) {
      return weekday === 0 ? 7 : weekday
    },
    isClosedDay(date, closedDates) {
      return Array.isArray(closedDates) && closedDates.includes(date.toFormat('yyyy-MM-dd'))
    },
    getNextSeasonalOpeningTime(currentDateTime, nextDay, seasonalHours) {
      if (!Array.isArray(seasonalHours)) {
        return null
      }

      const currentStartOfDay = currentDateTime.startOf('day').toMillis()
      const currentEndOfDay = currentDateTime.endOf('day').toMillis()
      const nextStartOfDay = nextDay.startOf('day').toMillis()
      const nextEndOfDay = nextDay.endOf('day').toMillis()

      for (const seasonalHour of seasonalHours) {
        const startTimeInMillis = seasonalHour.startTimeInMillis

        // If startTimeInMillis is for today we check if current time is before startTimeInMillis
        // If so we return text and hours for startTimeInMillis
        if (startTimeInMillis >= currentStartOfDay && startTimeInMillis <= currentEndOfDay) {
          if (startTimeInMillis > currentDateTime.toMillis()) {
            return {
              date: i18n.global.t('today'),
              time: DateTime.fromMillis(startTimeInMillis).toFormat('HH:mm'),
            }
          }
        }
        // If startTimeInMillis falls on tomorrow we return date and time for startTimeInMillis
        if (startTimeInMillis >= nextStartOfDay && startTimeInMillis <= nextEndOfDay) {
          return {
            date: i18n.global.t('tomorrow'),
            time: DateTime.fromMillis(startTimeInMillis).toFormat('HH:mm'),
          }
        }
      }

      return null
    },
    getNextWeekdayOpeningTime(currentDateTime, nextDay, weekdayHours, timezone) {
      if (!Array.isArray(weekdayHours)) {
        return null
      }

      const currentWeekday = currentDateTime.weekday
      const nextWeekday = nextDay.weekday
      let openToday = false
      let openTomorrow = false
      let time = ''
      let currentNextTimeInMillis = null

      for (const weekdayHour of weekdayHours) {
        if (!weekdayHour) {
          continue
        }
        let weekday = Number(weekdayHour.day)
        if (isNaN(weekday)) {
          continue
        }

        if (weekday === 0) {
          weekday = 7
        }

        if (weekday === currentWeekday) {
          const startHours = this.parseDateTimeFromHours(weekdayHour.starthours, currentDateTime, timezone)
          if (
            currentDateTime.toMillis() < startHours.toMillis() &&
            (!currentNextTimeInMillis || currentNextTimeInMillis > startHours.toMillis())
          ) {
            openToday = true
            time = startHours.toFormat('HH:mm')
            currentNextTimeInMillis = startHours.toMillis()
          }
        }

        if (!openToday && weekday === nextWeekday) {
          const startHours = this.parseDateTimeFromHours(weekdayHour.starthours, nextDay, timezone)
          if (currentDateTime.toMillis() < startHours.toMillis()) {
            openTomorrow = true
            time = startHours.toFormat('HH:mm')
          }
        }
      }

      if (openToday || openTomorrow) {
        return {
          date: openToday ? i18n.global.t('today') : i18n.global.t('tomorrow'),
          time,
        }
      }

      return null
    },
    getAdjustedDateTime(timezone) {
      return this.systemTimeInMillis ? DateTime.fromMillis(this.systemTimeInMillis) : DateTime.local({ zone: timezone })
    },
    async fetchAndUpdateLocationBlocks() {
      if (!this.kiosk?.organization?.uuid) {
        return null
      }

      try {
        const result = await apolloClient.query({
          query: GET_LOCATION_BLOCKS,
          variables: {
            organization: this.kiosk.organization.uuid,
          },
          fetchPolicy: 'no-cache',
        })

        if (result?.data) {
          const { itemLocationBlocks, variationLocationBlocks } = result.data

          if (itemLocationBlocks) {
            this.kiosk.itemLocationBlocks = itemLocationBlocks
          }

          if (variationLocationBlocks) {
            this.kiosk.variationLocationBlocks = variationLocationBlocks
          }

          return result.data
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            module: 'store',
            function: 'getLocationBlocks',
          },
        })
      }
      return null
    },
    // Coupon actions
    openCouponDialog() {
      this.showCouponDialog = true
      this.couponError = null
    },
    closeCouponDialog() {
      this.showCouponDialog = false
      this.couponError = null
      this.couponValidating = false
    },
    async applyCoupon(couponCode) {
      if (!couponCode || !couponCode.trim()) {
        this.couponError = 'Please enter a coupon code'
        return false
      }

      if (!this.order?.lineItems?.length) {
        this.couponError = 'No items in cart to apply coupon to'
        return false
      }

      this.couponValidating = true
      this.couponError = null

      try {
        // First, create a new cart array with generated UUIDs
        const cartWithUUIDs = this.order.lineItems.map((item) => ({
          ...item,
          redemptionUUID: uuidV4(),
        }))

        // Now construct the line items using the attached UUIDs
        const lineItems = cartWithUUIDs.map((item) => {
          const itemPrice = this.getItemCurrentPrice(item, item.quantity, item.selectedVariations)
          return {
            uuid: item.redemptionUUID, // Use the UUID we attached to the cart item
            item: item.uuid,
            quantity: item.quantity || 1,
            totalPrice: itemPrice.amount || 0,
          }
        })

        const variables = {
          organization: this.kiosk.organization.uuid,
          coupon: couponCode,
          orderReference: {
            organization: this.kiosk.organization.uuid,
            location: this.order.selectedLocation?.uuid,
            lineItems: lineItems,
          },
        }

        const body = {
          query: `
            mutation CalculateCouponRedemptions(
              $organization: String!,
              $coupon: String!,
              $orderReference: CouponOrderReference
            ) {
              calculateCouponRedemptions(
                organization: $organization,
                coupon: $coupon,
                orderReference: $orderReference
              ) {
               uuid
               order
               lineItems {
                uuid
                discountPercent
                totalPrice
                apply
                quantity
               }
               coupon {
                uuid
                code
               }
              }
            }
          `,
          variables,
        }

        const response = await axios.post(this.graphUrl, body)
        const result = response.data.data.calculateCouponRedemptions

        // Check if coupon is invalid based on the response structure
        // Invalid coupons return: coupon: null and uuid: "" (empty string)
        if (!result || result.coupon === null || result.uuid === '') {
          this.couponError = 'Invalid coupon code'
          return false
        }

        // Update cart items with discount information
        const updatedLineItems = cartWithUUIDs.map((cartItem) => {
          // Find matching line item from response using our attached UUID
          const matchingLineItem = result.lineItems.find((lineItem) => lineItem.uuid === cartItem.redemptionUUID)

          if (!matchingLineItem || matchingLineItem.apply === 'none') {
            return {
              ...cartItem,
              discountPercent: 0,
              discountAmount: null,
            }
          }

          if (matchingLineItem) {
            const originalPrice = this.getItemCurrentPrice(cartItem, cartItem.quantity, cartItem.selectedVariations)
            const discountPercent = matchingLineItem.discountPercent || 0

            return {
              ...cartItem,
              discountPercent: discountPercent,
              discountAmount:
                discountPercent > 0
                  ? {
                      amount: Math.round(originalPrice.amount * (discountPercent / 100)),
                      currency_code: originalPrice.currency_code,
                    }
                  : null,
              // Store the discounted total price from the response
              discountedTotalPrice:
                matchingLineItem.totalPrice >= 0
                  ? {
                      amount: matchingLineItem.totalPrice,
                      currency_code: originalPrice.currency_code,
                    }
                  : null,
              // Remove the redemptionUUID as it's no longer needed
              redemptionUUID: undefined,
            }
          }
          return cartItem
        })

        // Update the order with discounted line items and coupon info
        this.order.lineItems = updatedLineItems

        if (result.uuid) {
          this.couponCalculation = result.uuid
          this.couponCode = couponCode // Store the successful coupon code
        }

        this.customLogger('info', 'Coupon applied successfully', {
          couponCode,
          redemptionUUID: result.uuid,
          couponUUID: result.coupon?.uuid,
        })

        return true
      } catch (error) {
        this.customLogger('error', 'Coupon validation error', error)

        if (error.response?.data?.errors?.length > 0) {
          this.couponError = error.response.data.errors[0].message || 'Invalid coupon code'
        } else {
          this.couponError = 'Failed to validate coupon. Please try again.'
        }

        return false
      } finally {
        this.couponValidating = false
      }
    },
    removeCoupon() {
      if (this.couponCalculation) {
        this.couponCalculation = null
        this.couponCode = ''

        // Clear discount information from line items
        if (this.order.lineItems) {
          this.order.lineItems = this.order.lineItems.map((item) => ({
            ...item,
            discountPercent: 0,
            discountAmount: null,
            discountedTotalPrice: null,
          }))
        }

        this.customLogger('info', 'Coupon removed from order')
      }
    },
    initializeWatchers() {
      let previousLineItemsSnapshot = null

      watch(
        () => this.order?.lineItems,
        (newLineItems) => {
          if (!previousLineItemsSnapshot && newLineItems) {
            previousLineItemsSnapshot = newLineItems.map((item) => ({
              uuid: item.uuid,
              quantity: item.quantity,
            }))
            return
          }

          if (this.couponCalculation && this.couponCode && newLineItems && previousLineItemsSnapshot) {
            const lengthChanged = newLineItems.length !== previousLineItemsSnapshot.length

            let quantityChanged = false
            if (!lengthChanged && newLineItems.length === previousLineItemsSnapshot.length) {
              for (let i = 0; i < newLineItems.length; i++) {
                if (newLineItems[i].quantity !== previousLineItemsSnapshot[i].quantity) {
                  quantityChanged = true
                  break
                }
              }
            }

            // Only recalculate if length changed or quantity changed
            if (lengthChanged || quantityChanged) {
              this.recalculateActiveCoupon()
            }

            // Update the snapshot for next comparison (only when we have active coupon)
            previousLineItemsSnapshot = newLineItems.map((item) => ({
              uuid: item.uuid,
              quantity: item.quantity,
            }))
          }
        },
        {
          deep: true,
          flush: 'post',
        }
      )
    },
    async recalculateActiveCoupon() {
      if (!this.couponCalculation || !this.couponCode) {
        return
      }

      clearTimeout(this._couponRecalcTimeout)
      this._couponRecalcTimeout = setTimeout(async () => {
        try {
          const currentCouponCode = this.couponCode

          const success = await this.applyCoupon(currentCouponCode)

          if (!success) {
            this.removeCoupon()
          }
        } catch (error) {
          this.removeCoupon()
        }
      }, 500)
    },
  },
})
